import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Select, 
  Table,
  Tag, 
  Typography, 
  App, 
  Spin, 
  Pagination, 
  Empty, 
  DatePicker, 
  Tooltip, 
  Input, 
  Drawer,
  Radio,
  Upload,
  Checkbox
} from 'antd';
import { 
  DownloadOutlined, 
  ReloadOutlined,
  PlusOutlined,
  CloudDownloadOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  EyeOutlined,
  EditOutlined,
  FieldTimeOutlined,
  DollarOutlined,
  GiftOutlined,
  UploadOutlined,
  CloudUploadOutlined,
  CloudServerOutlined,
  HourglassOutlined,
  LoadingOutlined,
  CodeOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { 
  getTeacherList, 
  getHistoryClasses, 
  getDownloadTasks, 
  createDownloadTask,
  deleteDownloadTask,
  batchDeleteDownloadTasks,
  queryVideoTask,
  getSignedDownloadUrl,
  getSignedDownloadUrlForDownload,
  updateCourseArchiveInfo,
  getCombinedTeachers,
  addDeletedTeacher,
  deleteDeletedTeacher
} from '../../services/plasoService';
import axiosInstance from '../../services/axiosInstance';
import moment from 'moment';
import { downloadLogsApi } from '../../services/downloadLogsService';
import { useAuth } from '../../contexts/AuthContext';
import { formatDuration } from '../../utils/formatUtils';

const { Title } = Typography;

// 优化的文件名输入组件
const FilenameInput = React.memo(({ 
  record, 
  isDuplicate, 
  onFilenameChange 
}) => {
  const currentFilename = record.filename || record.short_Desc || '';
  
  return (
    <Input
      value={currentFilename}
      onChange={(e) => {
        const newFilename = e.target.value;
        // 防止输入为空时自动重置为默认值
        if (newFilename === '' && currentFilename !== '') {
          // 允许清空但不立即重置为默认值
          onFilenameChange(record.recordId, '');
        } else {
          onFilenameChange(record.recordId, newFilename);
        }
      }}
      onBlur={(e) => {
        // 失去焦点时，如果为空则设置为默认值
        const value = e.target.value.trim();
        if (value === '') {
          const defaultFilename = record.short_Desc || '未命名课堂';
          onFilenameChange(record.recordId, defaultFilename);
        }
      }}
      placeholder="请输入存档文件名"
      allowClear
      status={isDuplicate ? 'error' : ''}
      style={{
        backgroundColor: isDuplicate ? '#fff2f0' : '',
        borderColor: isDuplicate ? '#ff4d4f' : ''
      }}
    />
  );
});

// 添加样式
const teacherSelectStyles = `
  .teacher-select-dropdown .ant-select-dropdown {
    position: relative !important;
  }
  
  .teacher-select-dropdown .ant-select-dropdown::after {
    content: '';
    position: absolute;
    right: 8px;
    top: 8px;
    bottom: 8px;
    width: 20px;
    background: linear-gradient(to bottom, 
      #f0f0f0 0%, #f0f0f0 8.33%, transparent 8.33%, transparent 16.66%,
      #f0f0f0 16.66%, #f0f0f0 25%, transparent 25%, transparent 33.33%,
      #f0f0f0 33.33%, #f0f0f0 41.66%, transparent 41.66%, transparent 50%,
      #f0f0f0 50%, #f0f0f0 58.33%, transparent 58.33%, transparent 66.66%,
      #f0f0f0 66.66%, #f0f0f0 75%, transparent 75%, transparent 83.33%,
      #f0f0f0 83.33%, #f0f0f0 91.66%, transparent 91.66%, transparent 100%
    );
    border-radius: 10px;
    pointer-events: none;
    z-index: 1000;
  }
  
  .alphabet-index {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 10px;
    padding: 4px 2px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1001;
    pointer-events: none;
  }
  
  .alphabet-index .letter {
    font-size: 10px;
    font-weight: bold;
    color: #666;
    line-height: 14px;
    padding: 1px 4px;
    border-radius: 2px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }
  
  .alphabet-index .letter.active {
    background: #284D40;
    color: white;
  }
`;

// 注入样式
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('teacher-select-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'teacher-select-styles';
    style.textContent = teacherSelectStyles;
    document.head.appendChild(style);
  }
}

const DownloadList = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  // 筛选和搜索相关状态
  const [filters, setFilters] = useState({
    grade: undefined,
    classType: undefined,
    course: undefined,
    searchText: '',
    teacher_id: '',
    teacher_name: '',
    status: undefined,  // 添加状态筛选
    custom_tag: undefined  // 添加标签筛选
  });
  
  // 新增：排序相关状态
  const [sortOrder, setSortOrder] = useState('course_time_desc'); // 默认按上课时间倒序
  // 可选值：'course_time_desc', 'course_time_asc', 'create_time_desc', 'create_time_asc', 'finish_time_desc', 'finish_time_asc'
  
  // 远程存档任务相关状态
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadForm] = Form.useForm();
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [teacherListLoading, setTeacherListLoading] = useState(false);
  
  // 新增：上传视频相关状态
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadForm] = Form.useForm();
  const [selectedUploadClass, setSelectedUploadClass] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isMicroClass, setIsMicroClass] = useState(false); // 是否为微课上传模式
  const [videoDuration, setVideoDuration] = useState(0); // 视频时长
  
  // 监听表单字段变化
  const gradeNameValue = Form.useWatch('grade_name', downloadForm);
  const classNameValue = Form.useWatch('class_name', downloadForm);
  const courseNameValue = Form.useWatch('course_name', downloadForm);
  const teacherIdValue = Form.useWatch('teacher_id', downloadForm);
  
  // 监听上传表单字段变化
  const uploadGradeNameValue = Form.useWatch('grade_name', uploadForm);
  const uploadClassNameValue = Form.useWatch('class_name', uploadForm);
  const uploadCourseNameValue = Form.useWatch('course_name', uploadForm);
  const uploadTeacherIdValue = Form.useWatch('teacher_id', uploadForm);
  const uploadFilenameValue = Form.useWatch('filename', uploadForm);
  
  // 历史课堂相关状态
  const [historyClasses, setHistoryClasses] = useState([]);
  const [historyClassesLoading, setHistoryClassesLoading] = useState(false);
  const [historyClassesPagination, setHistoryClassesPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedHistoryClasses, setSelectedHistoryClasses] = useState([]);
  
  // 编辑课堂存档信息相关状态
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [currentEditRecord, setCurrentEditRecord] = useState(null);
  
  // 移除定时刷新相关状态
  
  // 详情抽屉相关状态
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  
  // 批量操作相关状态
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  
  // 批量编辑相关状态
  const [batchEditModalVisible, setBatchEditModalVisible] = useState(false);
  const [batchEditForm] = Form.useForm();
  const [clearTagsChecked, setClearTagsChecked] = useState(false);
  
  // 新增：标签选项
  const [tagOptions, setTagOptions] = useState([]);
  const [tagLoading, setTagLoading] = useState(false);
  
  // 新增：下载模式选择状态
  const [downloadMode, setDownloadMode] = useState('api'); // 'plaso' 或 'api'，默认选择自建API
  
  // 新增：下载模式确认弹窗状态
  const [downloadModeModalVisible, setDownloadModeModalVisible] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  // 新增：自建API可用性状态
  const [apiStatus, setApiStatus] = useState({ 
    available: true, 
    remainingTasks: null, 
    maxTasks: null,
    message: '' 
  }); // 自建API状态信息

  // 新增：下载任务抽屉相关状态
  const [downloadTaskDrawerVisible, setDownloadTaskDrawerVisible] = useState(false);
  const [downloadingTasks, setDownloadingTasks] = useState([]);
  const [downloadingTasksLoading, setDownloadingTasksLoading] = useState(false);
  const [downloadingTasksPagination, setDownloadingTasksPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const { message, modal } = App.useApp();

  // 届次选项
  const gradeOptions = [
    { value: '2025届', label: '2025届' },
    { value: '2026届', label: '2026届' },
    { value: '2027届', label: '2027届' },
    { value: '2028届', label: '2028届' },
    { value: '测试届次', label: '测试届次' },
  ];
  
  // 班次选项
  const classOptions = [
    { value: '全程班', label: '全程班' },
    { value: '暑假班', label: '暑假班' },
    { value: '寒假班', label: '寒假班' },
    { value: '点题班', label: '点题班' },
    { value: '全日制班', label: '全日制班' },
    { value: '测试班次', label: '测试班次' },
  ];
  
  // 课程选项
  const courseOptions = [
    { value: '高等数学', label: '高等数学' },
    { value: '大学语文', label: '大学语文' },
    { value: '英语四级等级考试', label: '英语四级等级考试' },
    { value: '日语四级等级考试', label: '日语四级等级考试' },
    { value: '计算机一级等级考试', label: '计算机一级等级考试' },
    { value: '管理类', label: '管理类' },
    { value: '财经类', label: '财经类' },
    { value: '电子信息类', label: '电子信息类' },
    { value: '化工生物类', label: '化工生物类' },
    { value: '机械工程类', label: '机械工程类' },
    { value: '土木建筑类', label: '土木建筑类' },
    { value: '计算机类', label: '计算机类' },
    { value: '美术设计类', label: '美术设计类' },
    { value: '环境资源类', label: '环境资源类' },
    { value: '教育类', label: '教育类' },
    { value: '农林类', label: '农林类' },
    { value: '英语类', label: '英语类' },
    { value: '新闻传播类', label: '新闻传播类' },
    { value: '音乐类', label: '音乐类' },
    { value: '日语类', label: '日语类' },
    { value: '法学类', label: '法学类' },
    { value: '食品类', label: '食品类' },
    { value: '文史类', label: '文史类' },
    { value: '医护类', label: '医护类' },
    { value: '测试课程', label: '测试课程' },
  ];

  // 状态选项 - 移除下载中和等待中的状态，因为这些会在抽屉中显示
  const statusOptions = [
    { value: '99', label: '成功' },
    { value: '-1', label: '转码失败' },
  ];

  // 获取所有标签
  const fetchTagOptions = useCallback(async () => {
    setTagLoading(true);
    try {
      // 使用项目统一的axiosInstance
      const response = await axiosInstance.get('plaso/custom_tags');
      
      // axiosInstance已经处理过响应，直接检查返回的数据结构
      if (response && response.code === 0) {
        const tagsData = response.data;
        
        // 检查是否是包装后的格式（有items字段）
        if (tagsData && Array.isArray(tagsData.items)) {
          const options = tagsData.items.map(tag => ({ value: tag.tag_name, label: tag.tag_name }));
          setTagOptions(options);
        } 
        // 检查是否是直接的数组格式
        else if (Array.isArray(tagsData)) {
          const options = tagsData.map(tag => ({ value: tag.tag_name, label: tag.tag_name }));
          setTagOptions(options);
        } else {
          console.warn('标签数据格式异常:', tagsData);
          setTagOptions([]);
        }
      } else {
        console.warn('标签接口返回格式异常:', response);
        setTagOptions([]);
      }
    } catch (error) {
      console.error('获取标签列表失败:', error);
      message.error('获取标签列表失败');
      setTagOptions([]);
    } finally {
      setTagLoading(false);
    }
  }, [message]);

  // 检查自建API可用性（预留函数，为以后扩展使用）
  const checkApiAvailability = async () => {
    try {
      // TODO: 调用后端API检查自建API的可用性和任务限制
      // const response = await axiosInstance.get('/plaso/api-status');
      // if (response.code === 0) {
      //   setApiStatus({
      //     available: response.data.available,
      //     remainingTasks: response.data.remainingTasks,
      //     maxTasks: response.data.maxTasks,
      //     message: response.data.message || ''
      //   });
      //   setApiAvailable(response.data.available);
      // }
      
      // 临时代码：默认设置为可用
      setApiStatus({
        available: true,
        remainingTasks: null,
        maxTasks: null,
        message: ''
      });

    } catch (error) {
      console.error('检查自建API可用性失败:', error);
      // 发生错误时，设置为不可用
      setApiStatus({
        available: false,
        remainingTasks: 0,
        maxTasks: null,
        message: '检查API状态失败'
      });

    }
  };

  // 获取下载中和等待中的任务
  const fetchDownloadingTasks = async (page = 1, pageSize = 20) => {
    setDownloadingTasksLoading(true);
    try {
      // 获取状态为0,1,2,3,4的任务（下载中和等待中）
      const response = await getDownloadTasks(page, pageSize, { 
        status_in: '0,1,2,3,4',
        sort_order: 'create_time_desc' // 按存档时间倒序
      });
      
      if (response.data && response.data.items) {
        const processedItems = response.data.items.map(item => ({
          ...item,
          duration: item.duration ? Number(item.duration) : 0
        }));
        setDownloadingTasks(processedItems);
        
        // 设置分页状态
        setDownloadingTasksPagination({
          current: page,
          pageSize: pageSize,
          total: response.data.total || 0,
        });
      } else {
        setDownloadingTasks([]);
        setDownloadingTasksPagination({
          current: 1,
          pageSize: 20,
          total: 0,
        });
      }
    } catch (error) {
      console.error('获取下载中任务列表出错:', error);
      setDownloadingTasks([]);
      setDownloadingTasksPagination({
        current: 1,
        pageSize: 20,
        total: 0,
      });
    } finally {
      setDownloadingTasksLoading(false);
    }
  };

  // 获取存档任务列表
  const fetchDownloadTasks = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const requestedPage = page;
      // 将排序参数传递给后端，并过滤掉下载中和等待中的任务
      const filtersWithSort = {
        ...filters,
        sort_order: sortOrder,
        status_exclude: '0,1,2,3,4' // 排除下载中和等待中的状态
      };
      const response = await getDownloadTasks(page, pageSize, filtersWithSort);
      
      if (response.data) {
        const { items, total } = response.data;
        
        // 检查是否为空结果且请求非首页
        if (requestedPage > 1 && (!items || items.length === 0)) {
          // 如果请求的不是第一页，但返回空数据，自动回到第一页
          await fetchDownloadTasks(1, pageSize);
          return;
        }
        
        // 确保duration字段被正确处理
        const processedItems = items.map(item => ({
          ...item,
          duration: item.duration ? Number(item.duration) : 0
        }));
        
        setData(processedItems);
        
        // 设置分页状态
        setPagination({
          ...pagination,
          current: requestedPage,
          pageSize,
          total: total || 0,
        });
      } else {
        message.error('获取存档任务列表失败');
      }
    } catch (error) {
      console.error('获取存档任务列表出错:', error);
      message.error('获取存档任务列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理筛选条件变化
  const handleFilterChange = (changedValues) => {
    // 当筛选条件变化时，重置到第一页，但保留页面大小
    setFilters(prev => ({
      ...prev,
      ...changedValues
    }));
    setPagination(prev => ({
      ...prev,
      current: 1 // 重置到第一页
    }));
  };

  // 处理搜索
  const handleSearch = (value) => {
    // 搜索时重置到第一页，但保留页面大小
    setFilters(prev => ({
      ...prev,
      searchText: value
    }));
    setPagination(prev => ({
      ...prev,
      current: 1 // 重置到第一页
    }));
  };

  // 重置筛选和搜索
  const handleReset = () => {
    // 重置所有筛选条件并回到第一页，但保留页面大小
    setFilters({
      grade: undefined,
      classType: undefined,
      course: undefined,
      searchText: '',
      teacher_id: '',
      teacher_name: '',
      status: undefined,
      custom_tag: undefined
    });
    // 重置排序为默认状态
    setSortOrder('course_time_desc');
    setPagination(prev => ({
      ...prev,
      current: 1 // 重置到第一页
    }));
  };

  // 监听筛选条件变化
  useEffect(() => {
    // 创建一个函数，避免连续多次请求
    const fetchData = () => {
      fetchDownloadTasks(pagination.current, pagination.pageSize);
    };
    
    // 调用函数
    fetchData();
    
    // 只关注真正需要的依赖项
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.current, pagination.pageSize, sortOrder]); // 添加sortOrder依赖

  // 组件挂载时获取标签选项和下载中任务
  useEffect(() => {
    fetchTagOptions();
    fetchDownloadingTasks();
  }, [fetchTagOptions]);

  // 移除自动刷新倒计时逻辑

  // 手动刷新
  const handleManualRefresh = () => {
    // 保持当前页码进行刷新，不要重置到第一页
    fetchDownloadTasks(pagination.current, pagination.pageSize);
    // 同时刷新下载中任务
    fetchDownloadingTasks();
  };

  // 获取教师列表
  const fetchTeacherList = async () => {
    setTeacherListLoading(true);
    try {
      // 使用新的合并教师列表API
      const response = await getCombinedTeachers();
      
      if (response.code === 0 && Array.isArray(response.data)) {
        const teacherData = response.data;
        const options = teacherData.map(teacher => ({
          value: teacher.teacher_id,
          label: teacher.teacher_name,
          name: teacher.teacher_name,
          is_deleted: teacher.is_deleted, // 添加已删除标记
          // 保留API教师的原始数据
          ...(teacher.api_data ? { 
            loginName: teacher.api_data.loginName,
            api_data: teacher.api_data 
          } : {})
        }));
        
        // 获取中文首字母的函数
        const getFirstLetter = (str) => {
          if (!str) return '#';
          const char = str.charAt(0);
          
          // 如果是英文字母，直接返回大写
          if (/[A-Za-z]/.test(char)) {
            return char.toUpperCase();
          }
          
          // 如果是数字，返回 #
          if (/[0-9]/.test(char)) {
            return '#';
          }
          
          // 使用完整的中文拼音首字母映射表（包含常见姓氏）
          const pinyinMap = {
            // A
            '安': 'A', '艾': 'A', '敖': 'A',
            // B  
            '白': 'B', '包': 'B', '鲍': 'B', '贝': 'B', '贲': 'B', '边': 'B', '别': 'B', '卞': 'B', '步': 'B', '班': 'B', '巴': 'B', '弓': 'B', '毕': 'B',
            // C
            '陈': 'C', '财': 'C', '曹': 'C', '褚': 'C', '常': 'C', '车': 'C', '成': 'C', '程': 'C', '崔': 'C', '蔡': 'C', '柴': 'C', '充': 'C', '查': 'C', '晁': 'C',
            // D
            '电': 'D', '杜': 'D', '戴': 'D', '董': 'D', '邓': 'D', '丁': 'D', '都': 'D', '段': 'D', '东': 'D', '窦': 'D', '堵': 'D',
            // E
            '鄂': 'E',
            // F
            '法': 'F', '冯': 'F', '费': 'F', '范': 'F', '方': 'F', '房': 'F', '富': 'F', '凤': 'F', '符': 'F', '傅': 'F', '封': 'F', '伏': 'F', '扶': 'F',
            // G
            '管': 'G', '郭': 'G', '葛': 'G', '高': 'G', '龚': 'G', '顾': 'G', '关': 'G', '古': 'G', '甘': 'G', '戈': 'G', '巩': 'G', '勾': 'G', '公': 'G', '盖': 'G', '广': 'G', '国': 'G',
            // H
            '化': 'H', '何': 'H', '韩': 'H', '华': 'H', '黄': 'H', '胡': 'H', '霍': 'H', '和': 'H', '洪': 'H', '侯': 'H', '花': 'H', '怀': 'H', '惠': 'H', '红': 'H', '荆': 'H', '後': 'H',
            // J
            '机': 'J', '集': 'J', '计': 'J', '记': 'J', '技': 'J', '江': 'J', '教': 'J', '蒋': 'J', '金': 'J', '姜': 'J', '纪': 'J', '季': 'J', '贾': 'J', '简': 'J', '景': 'J', '井': 'J', '吉': 'J', '嵇': 'J', '焦': 'J', '居': 'J', '家': 'J', '靳': 'J', '汲': 'J', '籍': 'J',
            // K
            '开': 'K', '孔': 'K', '康': 'K', '柯': 'K', '寇': 'K', '夔': 'K',
            // L
            '老': 'L', '罗': 'L', '理': 'L', '连': 'L', '联': 'L', '林': 'L', '路': 'L', '刘': 'L', '李': 'L', '吕': 'L', '朱': 'L', '梁': 'L', '廉': 'L', '乐': 'L', '柳': 'L', '鲁': 'L', '卢': 'L', '陆': 'L', '娄': 'L', '蓝': 'L', '雷': 'L', '利': 'L', '劳': 'L', '逄': 'L', '隆': 'L',
            // M
            '马': 'M', '美': 'M', '毛': 'M', '孟': 'M', '梅': 'M', '穆': 'M', '莫': 'M', '苗': 'M', '麻': 'M', '缪': 'M', '米': 'M', '明': 'M', '满': 'M', '匡': 'M', '牧': 'M', '宓': 'M', '糜': 'M', '蒙': 'M', '慕': 'M', '毋': 'M',
            // N
            '南': 'N', '内': 'N', '倪': 'N', '钮': 'N', '聂': 'N', '那': 'N', '宁': 'N', '牛': 'N', '农': 'N',
            // O
            '欧': 'O', '殴': 'O',
            // P
            '潘': 'P', '彭': 'P', '皮': 'P', '平': 'P', '庞': 'P', '裴': 'P', '蒲': 'P', '濮': 'P', '蓬': 'P', '浦': 'P',
            // Q
            '钱': 'Q', '秦': 'Q', '戚': 'Q', '齐': 'Q', '邱': 'Q', '瞿': 'Q', '乔': 'Q', '全': 'Q', '裘': 'Q', '祁': 'Q', '强': 'Q', '仇': 'Q', '权': 'Q', '阙': 'Q',
            // R
            '任': 'R', '阮': 'R', '饶': 'R', '戎': 'R', '荣': 'R', '冉': 'R', '芮': 'R', '羿': 'R', '日': 'R',
            // S
            '孙': 'S', '沈': 'S', '施': 'S', '苏': 'S', '史': 'S', '宋': 'S', '舒': 'S', '邵': 'S', '盛': 'S', '石': 'S', '司': 'S', '水': 'S', '松': 'S', '山': 'S', '师': 'S', '申': 'S', '单': 'S', '时': 'S', '韶': 'S', '索': 'S', '苍': 'S', '双': 'S', '舄': 'S', '桑': 'S', '寿': 'S', '尚': 'S', '沙': 'S', '须': 'S', '食': 'S',
            // T
            '唐': 'T', '陶': 'T', '田': 'T', '汤': 'T', '滕': 'T', '谈': 'T', '童': 'T', '屠': 'T', '谭': 'T', '翟': 'T', '通': 'T', '茅': 'T', '终': 'T', '太': 'T', '土': 'T',
            // W
            '王': 'W', '吴': 'W', '魏': 'W', '韦': 'W', '万': 'W', '武': 'W', '文': 'W', '温': 'W', '汪': 'W', '卫': 'W', '邬': 'W', '危': 'W', '翁': 'W', '巫': 'W', '乌': 'W', '沃': 'W', '隗': 'W',
            // X
            '徐': 'X', '许': 'X', '谢': 'X', '萧': 'X', '夏': 'X', '熊': 'X', '项': 'X', '邢': 'X', '荀': 'X', '席': 'X', '奚': 'X', '相': 'X', '习': 'X', '辛': 'X', '宣': 'X', '宦': 'X', '咸': 'X', '续': 'X', '胥': 'X', '宿': 'X', '新': 'X',
            // Y
            '杨': 'Y', '殷': 'Y', '于': 'Y', '袁': 'Y', '尤': 'Y', '俞': 'Y', '姚': 'Y', '尹': 'Y', '严': 'Y', '颜': 'Y', '余': 'Y', '元': 'Y', '叶': 'Y', '羊': 'Y', '於': 'Y', '伊': 'Y', '仰': 'Y', '印': 'Y', '阴': 'Y', '欎': 'Y', '燕': 'Y', '晏': 'Y', '阎': 'Y', '雍': 'Y', '易': 'Y', '游': 'Y', '逯': 'Y', '益': 'Y', '养': 'Y', '訾': 'Y', '阚': 'Y', '英': 'Y', '音': 'Y', '医': 'Y',
            // Z
            '赵': 'Z', '周': 'Z', '郑': 'Z', '张': 'Z', '邹': 'Z', '章': 'Z', '云': 'Z', '钟': 'Z', '左': 'Z', '祝': 'Z', '宗': 'Z', '臧': 'Z', '詹': 'Z', '束': 'Z', '龙': 'Z', '卓': 'Z', '蔺': 'Z', '郜': 'Z', '蓟': 'Z', '竺': 'Z', '支': 'Z', '昝': 'Z', '郁': 'Z', '杭': 'Z', '诸': 'Z', '祖': 'Z', '仲': 'Z', '暴': 'Z', '钭': 'Z', '厉': 'Z', '暨': 'Z', '庄': 'Z', '中': 'Z', '镇': 'Z', '资': 'Z'
          };
          
          // 直接使用映射表，如果没有找到则返回 '#'
          return pinyinMap[char] || '#';
        };

        // 为每个选项添加首字母信息并创建新对象，避免修改原始对象
        const optionsWithFirstLetter = options.map(option => ({
          ...option,
          firstLetter: getFirstLetter(option.name || option.loginName)
        }));

        // 按照首字母排序
        optionsWithFirstLetter.sort((a, b) => {
          if (a.firstLetter !== b.firstLetter) {
            return a.firstLetter.localeCompare(b.firstLetter);
          }
          const nameA = a.name || a.loginName || '';
          const nameB = b.name || b.loginName || '';
          return nameA.localeCompare(nameB, 'zh-CN', { 
            sensitivity: 'base',
            numeric: true 
          });
        });
        
        setTeacherOptions(optionsWithFirstLetter);
      } else {
        message.error('获取教师列表失败');
      }
    } catch (error) {
      console.error('获取教师列表出错:', error);
      message.error('获取教师列表失败');
    } finally {
      setTeacherListLoading(false);
    }
  };
  
  // 获取历史课堂列表
  const fetchHistoryClasses = useCallback(async (teacherId, page = 1, pageSize = 10) => {
    if (!teacherId) {
      console.warn('未选择教师');
      setHistoryClasses([]);
      setHistoryClassesPagination({
        current: 1,
        pageSize: 10,
        total: 0
      });
      return;
    }
    
    setHistoryClassesLoading(true);
    try {
      const response = await getHistoryClasses(teacherId, page, pageSize);
      
      // 检查响应数据结构
      if (!response || !response.data) {
        console.warn('响应数据为空:', response);
        setHistoryClasses([]);
        setHistoryClassesPagination({
          current: page,
          pageSize,
          total: 0
        });
        return;
      }
      
      // 检查是否是伯索平台连接错误
      if (response.code === -2 || response.code === -3) {
        // 连接伯索平台失败，显示错误信息
        message.error(`${response.message || '无法连接伯索平台服务'}`);
        
        // 即使连接错误，也返回空列表，允许上传功能
        setHistoryClasses([]);
        setHistoryClassesPagination({
          current: page,
          pageSize,
          total: 0
        });
        return;
      }

      // 直接使用response.data中的items数组
      const items = Array.isArray(response.data.items) ? response.data.items : [];
      
      // 适配数据结构，确保每个项目都有必要的字段
      const adaptedItems = items.map(item => ({
        ...item,
        recordId: item.recordId, // 只用recordId
        shortDesc: item.shortDesc || item.short_Desc || '', // 确保shortDesc存在
        has_download: item.has_download || false,
        download_status: item.download_status
      }));
      
      // 不再根据教师是否已删除显示特殊提示
      // 所有教师ID都应该能正常获取历史课堂
      
      setHistoryClasses(adaptedItems);
      setHistoryClassesPagination({
        current: response.data.page || page,
        pageSize: response.data.page_size || pageSize,
        total: response.data.total || 0
      });
    } catch (error) {
      console.error('获取历史课堂列表出错:', error);
      message.error('获取历史课堂列表失败，请检查网络连接');
      setHistoryClasses([]);
      setHistoryClassesPagination({
        current: page,
        pageSize,
        total: 0
      });
    } finally {
      setHistoryClassesLoading(false);
    }
  }, [message, teacherOptions]);

  // 表格变化处理
  const handleTableChange = (newPagination) => {
    // 只更新本地状态，不触发额外请求
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
    // 不调用fetchDownloadTasks，让useEffect处理
  };

  // 显示下载对话框
  const showDownloadModal = () => {
    setDownloadModalVisible(true);
    downloadForm.resetFields();
    fetchTeacherList();
    fetchTagOptions(); // 重新获取标签列表
    
    // 清空历史课堂数据
    setHistoryClasses([]);
    setHistoryClassesPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    setSelectedTeacherId('');
    setSelectedTeacher(null);  // 清空选中的教师信息
    setSelectedRowKeys([]);
    setSelectedHistoryClasses([]);
  };
  
  // 处理教师选择变化
  const handleTeacherChange = useCallback((value) => {
    // 批量更新状态，避免多次渲染
    setSelectedTeacherId(value);
    
    // 设置选中的教师信息，创建全新的对象避免循环引用
    const selectedTeacherInfo = teacherOptions.find(teacher => teacher.value === value);
    const newTeacherInfo = selectedTeacherInfo ? {
      userId: selectedTeacherInfo.value,
      fullname: selectedTeacherInfo.name || selectedTeacherInfo.label,
      is_deleted: selectedTeacherInfo.is_deleted || false // 添加已删除标记
    } : null;
    
    setSelectedTeacher(newTeacherInfo);
    
    // 清空已选择的历史课堂
    setSelectedRowKeys([]);
    setSelectedHistoryClasses([]);
    
    // 批量更新表单字段
    downloadForm.setFieldsValue({
      teacher_id: value,
      teacher_name: selectedTeacherInfo ? selectedTeacherInfo.name || selectedTeacherInfo.label : '',
      history_class_ids: []
    });
    
    // 获取该教师的历史课堂，无论是否已删除
    if (value) {
      // 无论是否已删除教师，都正常获取历史课堂信息
      // 已删除的教师ID也能正常获取历史课堂，不需要特殊提示
      fetchHistoryClasses(value, 1, 10);
    }
  }, [teacherOptions, downloadForm, fetchHistoryClasses, message]);
  
  // 处理历史课堂分页变化
  const handleHistoryClassesPaginationChange = useCallback((page) => {
    fetchHistoryClasses(selectedTeacherId, page, historyClassesPagination.pageSize);
  }, [selectedTeacherId, historyClassesPagination.pageSize, fetchHistoryClasses]);
  
  // 处理历史课堂选择
  const handleHistoryClassSelect = useCallback((selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
    
    // 获取当前页面的数据
    const currentPageData = historyClasses;
    const currentPageRecordIds = currentPageData.map(row => row.recordId);
    
    // 保留不在当前页面的已选择课程
    const classesFromOtherPages = selectedHistoryClasses.filter(cls => 
      !currentPageRecordIds.includes(cls.recordId)
    );
    
    // 获取当前页面的选中课程
    const currentPageSelectedClasses = [];
    selectedRowKeys.forEach(recordId => {
      if (currentPageRecordIds.includes(recordId)) {
        const currentPageRow = currentPageData.find(row => row.recordId === recordId);
        if (currentPageRow) {
          // 检查是否已经存在于selectedHistoryClasses中，如果存在则保留用户修改的filename
          const existingClass = selectedHistoryClasses.find(cls => cls.recordId === recordId);
          const filename = existingClass ? existingClass.filename : (currentPageRow.shortDesc || '未命名课堂');
          
          currentPageSelectedClasses.push({
            short_Desc: currentPageRow.shortDesc || '未命名课堂',
            recordId: currentPageRow.recordId,     // 伯索API使用
            fileId: currentPageRow.fileId,         // 自建API使用
            duration: currentPageRow.duration || 0,
            createTime: currentPageRow.createTime,
            filename: filename // 保留用户修改的filename或使用默认值
          });
        }
      }
    });
    
    // 合并其他页面的选择和当前页面的选择
    const newSelectedClasses = [...classesFromOtherPages, ...currentPageSelectedClasses];
    
    setSelectedHistoryClasses(newSelectedClasses);
  }, [historyClasses, selectedHistoryClasses]);

  // 判断是否禁用选择框
  const isDisabledCheckbox = (record) => {
    if (!record.has_download) {
      return false;
    }
    
    // 如果有存档任务，则检查状态
    const status = Number(record.download_status);
    
    // 如果状态是下载中(0-4)或下载成功(99)，则禁用选择
    if ((status >= 0 && status <= 4) || status === 99) {
      return true;
    }
    
    // 下载失败的可以选择
    return false;
  };

  // 历史课堂列表的rowSelection配置
  const historyClassesRowSelection = {
    selectedRowKeys: selectedRowKeys,
    // onChange: handleHistoryClassSelect, // 移除onChange，使用onSelect和onSelectAll完全控制
    getCheckboxProps: (record) => ({
      disabled: isDisabledCheckbox(record) || (selectedRowKeys.length >= 20 && !selectedRowKeys.includes(record.recordId))
    }),
    onSelect: (record, selected, selectedRows, nativeEvent) => {
      if (selected && selectedRowKeys.length >= 20) {
        message.warning('单次最多只能选择20个课堂进行批量存档');
        return false;
      }
      
      // 手动控制选择逻辑
      let newSelectedKeys;
      if (selected) {
        // 添加选择
        newSelectedKeys = [...selectedRowKeys, record.recordId];
      } else {
        // 移除选择
        newSelectedKeys = selectedRowKeys.filter(key => key !== record.recordId);
      }
      
      // 手动调用选择处理函数
      handleHistoryClassSelect(newSelectedKeys, []);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (selected) {
        const availableRows = changeRows.filter(row => !isDisabledCheckbox(row));
        const canSelectCount = Math.min(availableRows.length, 20 - selectedRowKeys.length);
        
        if (canSelectCount < availableRows.length) {
          message.warning(`单次最多只能选择20个课堂，已为您选择前${canSelectCount}个可用课堂`);
          
          // 手动选择前canSelectCount个可用课堂
          const newSelectedKeys = [...selectedRowKeys];
          
          for (let i = 0; i < canSelectCount; i++) {
            const row = availableRows[i];
            if (!newSelectedKeys.includes(row.recordId)) {
              newSelectedKeys.push(row.recordId);
            }
          }
          
          // 手动调用选择处理函数
          handleHistoryClassSelect(newSelectedKeys, []);
        } else {
          // 如果可以全选当前页面的所有可用行，直接添加到现有选择中
          const newSelectedKeys = [...selectedRowKeys];
          
          availableRows.forEach(row => {
            if (!newSelectedKeys.includes(row.recordId)) {
              newSelectedKeys.push(row.recordId);
            }
          });
          
          // 手动调用选择处理函数
          handleHistoryClassSelect(newSelectedKeys, []);
        }
      } else {
        // 取消选择当前页面的所有行，但保留其他页面的选择
        const currentPageRecordIds = historyClasses.map(row => row.recordId);
        const newSelectedKeys = selectedRowKeys.filter(key => !currentPageRecordIds.includes(key));
        
        // 手动调用选择处理函数
        handleHistoryClassSelect(newSelectedKeys, []);
      }
    }
  };

  // 主列表的rowSelection配置
  const mainTableRowSelection = {
    selectedRowKeys: selectedTaskIds,
    onChange: (selectedRowKeys) => {
      setSelectedTaskIds(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: Number(record.status) >= 0 && Number(record.status) <= 4, // 下载中的任务不可选
    }),
  };

  // 获取已选中课堂的总时长
  const getTotalDuration = () => {
    return selectedHistoryClasses.reduce((total, item) => {
      return total + (item.duration || 0);
    }, 0);
  };

  // 计算预估费用（向上归整到小时）
  const getEstimatedCost = () => {
    const totalSeconds = getTotalDuration();
    if (totalSeconds === 0) return 0;
    const totalHours = Math.ceil(totalSeconds / 3600); // 向上归整到小时
    return totalHours;
  };

  // 使用useMemo优化重复文件名检查
  const duplicateFilenames = useMemo(() => {
    const filenameCount = {};
    const duplicates = new Set();
    
    selectedHistoryClasses.forEach(cls => {
      const filename = cls.filename || cls.short_Desc || '';
      if (filename.trim()) {
        filenameCount[filename] = (filenameCount[filename] || 0) + 1;
        if (filenameCount[filename] > 1) {
          duplicates.add(filename);
        }
      }
    });
    
    return duplicates;
  }, [selectedHistoryClasses]);

  // 检查文件名是否重复
  const getDuplicateFilenames = () => {
    return duplicateFilenames;
  };

  // 判断文件名是否重复
  const isFilenameDuplicate = (filename) => {
    return duplicateFilenames.has(filename);
  };

  // 优化文件名更新性能 - 使用useCallback避免重复创建函数
  const updateFilename = useCallback((recordId, newFilename) => {
    setSelectedHistoryClasses(prevClasses => 
      prevClasses.map(cls => {
        if (cls.recordId === recordId) {
          return { ...cls, filename: newFilename };
        }
        return cls;
      })
    );
  }, []);

  // 简单的防抖函数
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // 防抖处理文件名更新，减少频繁的状态更新
  const debouncedUpdateFilename = useCallback(
    debounce((recordId, newFilename) => {
      updateFilename(recordId, newFilename);
    }, 300), // 300ms防抖
    [updateFilename]
  );

  // 文件名输入处理函数
  const handleFilenameChange = useCallback((recordId, value) => {
    // 立即更新本地状态以保证输入响应性
    setSelectedHistoryClasses(prevClasses => 
      prevClasses.map(cls => {
        if (cls.recordId === recordId) {
          return { ...cls, filename: value };
        }
        return cls;
      })
    );
  }, []);

  // 第一步：验证表单并显示下载模式选择弹窗
  const handleDownloadSubmit = async () => {
    try {
      const values = await downloadForm.validateFields();
      
      // 检查是否选择了历史课堂
      if (!selectedHistoryClasses || selectedHistoryClasses.length === 0) {
        message.error('请选择至少一个历史课堂');
        return;
      }
      
      // 检查文件名是否有重复
      const duplicates = getDuplicateFilenames();
      if (duplicates.size > 0) {
        const duplicateList = Array.from(duplicates).join('、');
        message.error(`存在重复的文件名：${duplicateList}，请修改后再提交`);
        return;
      }
      
      // 检查教师信息（表单验证已经检查了teacher_id，这里再次确认selectedTeacher状态）
      if (!selectedTeacher || !selectedTeacher.userId) {
        message.error('教师信息异常，请重新选择教师');
        return;
      }
      
      // 保存待提交的数据并显示下载模式选择弹窗
      setPendingSubmitData(values);
      setDownloadModeModalVisible(true);
      
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  // 第二步：确认下载模式后实际提交任务
  const handleConfirmDownloadMode = async () => {
    if (!pendingSubmitData) {
      message.error('提交数据异常，请重新操作');
      return;
    }

    try {
          // 不再区分教师是否已被删除，所有教师都可以创建存档任务

      // 检查自建API模式下是否所有课堂都有fileId
      if (downloadMode === 'api') {
        const missingFileId = selectedHistoryClasses.find(hc => !hc.fileId);
        if (missingFileId) {
          message.error('自开发API接口模式下，所有课堂都必须包含fileId字段，请检查选择的课堂');
          return;
        }
      }

      // 准备请求数据
      const baseRequestData = {
        teacher_id: selectedTeacher.userId,
        teacher_name: selectedTeacher.fullname,
        grade_name: pendingSubmitData.grade_name,
        class_name: pendingSubmitData.class_name,
        course_name: pendingSubmitData.course_name,
        custom_tags: pendingSubmitData.custom_tags || []  // 传递标签数组
      };
      
      let requestData;
      if (downloadMode === 'api') {
        // 自建API格式：使用fileId
        requestData = {
          ...baseRequestData,
          history_classes: selectedHistoryClasses.map(cls => ({
            short_Desc: cls.filename,  // 使用用户设置的文件名作为short_Desc
            fileId: cls.fileId,        // 自建API使用fileId
            duration: cls.duration || 0,
            createTime: cls.createTime
          }))
        };
      } else {
        // 伯索API格式：使用recordId
        requestData = {
          ...baseRequestData,
          history_classes: selectedHistoryClasses.map(cls => ({
            short_Desc: cls.filename,  // 使用用户设置的文件名作为short_Desc
            recordId: cls.recordId,    // 伯索API使用recordId
            duration: cls.duration || 0,
            createTime: cls.createTime
          })),
          is_local: false  // 表示远程下载（仅伯索API需要）
        };
      }
      
      setLoading(true);
      
      // 如果任务数量较多，给用户友好提示
      const taskCount = selectedHistoryClasses.length;
      if (taskCount >= 10) {
        message.loading(`正在创建${taskCount}个任务，预计需要${Math.ceil(taskCount / 4)}分钟，请耐心等待...`, 0);
      }
      
      // 根据下载模式选择不同的API端点
      let response;
      if (downloadMode === 'api') {
        // 调用自建API接口，设置动态超时时间
        const taskCount = selectedHistoryClasses.length;
        const timeout = Math.max(180000, taskCount * 10000); // 基础3分钟，每个任务额外10秒
        response = await axiosInstance.post('/plaso/create-download-api', requestData, {
          timeout: timeout
        });
      } else {
        // 调用伯索API接口
        response = await createDownloadTask(requestData);
      }
      
      // 清除加载提示
      message.destroy();
      
      if (response.code === 0) {
        if (response.data && response.data.success_count > 0) {
          const modeText = downloadMode === 'api' ? '自开发API' : '伯索API';
          message.success(`通过${modeText}成功创建${response.data.success_count}个存档任务`);
          // 关闭所有对话框，重置表单
          setDownloadModeModalVisible(false);
          setDownloadModalVisible(false);
          setPendingSubmitData(null);
          downloadForm.resetFields();
          setSelectedRowKeys([]);
          setSelectedHistoryClasses([]);
          setDownloadMode('api'); // 重置为默认模式（自建API）
          fetchDownloadTasks(pagination.current, pagination.pageSize);
          fetchDownloadingTasks(); // 刷新下载中任务列表
          fetchTagOptions(); // 重新获取标签列表，确保新创建的标签能显示
          return;
        } else {
          message.error(`所有任务创建失败：${(response.data?.failed_tasks || []).map(t => t.error).join(', ')}`);
          return;
        }
      } else {
        const modeText = downloadMode === 'api' ? '自开发API' : '伯索API';
        message.error(response.message || `通过${modeText}创建存档任务失败`);
        return;
      }
    } catch (error) {
      console.error('提交存档任务出错:', error);
      // 清除加载提示
      message.destroy();
      const modeText = downloadMode === 'api' ? '自开发API' : '伯索API';
      
      // 检查是否是超时错误，给出更友好的提示
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message.error(`${modeText}请求超时，任务可能仍在后台处理中。请稍后查看任务状态或联系管理员。`);
      } else {
        message.error(`通过${modeText}提交存档任务失败：${error.message || '未知错误'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // 取消下载模式选择
  const handleCancelDownloadMode = () => {
    setDownloadModeModalVisible(false);
    setPendingSubmitData(null);
  };

  // 显示上传对话框
  const showUploadModal = () => {
    setUploadModalVisible(true);
    uploadForm.resetFields();
    fetchTeacherList();
    fetchTagOptions();
    
    // 清空历史课堂数据
    setHistoryClasses([]);
    setHistoryClassesPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    setSelectedTeacherId('');
    setSelectedTeacher(null);
    setSelectedUploadClass(null);
    setUploadFile(null);
    setIsMicroClass(false); // 重置微课状态
    setVideoDuration(0); // 重置视频时长
  };

  // 处理上传教师选择变化
  const handleUploadTeacherChange = useCallback((value) => {
    setSelectedTeacherId(value);
    
    const selectedTeacherInfo = teacherOptions.find(teacher => teacher.value === value);
    const newTeacherInfo = selectedTeacherInfo ? {
      userId: selectedTeacherInfo.value,
      fullname: selectedTeacherInfo.name || selectedTeacherInfo.label,
      is_deleted: selectedTeacherInfo.is_deleted || false // 添加已删除标记
    } : null;
    
    setSelectedTeacher(newTeacherInfo);
    
    // 清空已选择的历史课堂
    setSelectedUploadClass(null);
    
    uploadForm.setFieldsValue({
      teacher_id: value,
      teacher_name: selectedTeacherInfo ? selectedTeacherInfo.name || selectedTeacherInfo.label : '',
      selected_class: null
    });
    
    // 获取该教师的历史课堂，无论是否已删除
    if (value) {
      // 无论是否已删除教师，都正常获取历史课堂信息
      // 已删除的教师ID也能正常获取历史课堂，不需要特殊提示
      fetchHistoryClasses(value, 1, 10);
    }
  }, [teacherOptions, uploadForm, fetchHistoryClasses]);

  // 处理上传历史课堂选择
  const handleUploadClassSelect = (classData) => {
    setSelectedUploadClass(classData);
    uploadForm.setFieldValue('filename', classData.shortDesc || '未命名课堂');
  };

  // 生成随机6位数字
  const generateRandomSuffix = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // 处理文件上传
  const handleFileUpload = (file) => {
    // 检查文件类型
    const isVideo = file.type.startsWith('video/');
    if (!isVideo) {
      message.error('只能上传视频文件！');
      return false;
    }
    
    // 检查文件大小（限制为2GB）
    const isLt2G = file.size / 1024 / 1024 / 1024 < 2;
    if (!isLt2G) {
      message.error('视频文件大小不能超过2GB！');
      return false;
    }
    
    setUploadFile(file);
    
    // 微课模式下，获取视频文件名作为课堂名称，并读取视频时长
    if (isMicroClass) {
      // 设置默认文件名（去除扩展名）
      const fileName = file.name.replace(/\.[^/.]+$/, "");
      uploadForm.setFieldsValue({
        filename: fileName
      });
      
      // 读取视频时长
      const videoElement = document.createElement('video');
      videoElement.preload = 'metadata';
      
      videoElement.onloadedmetadata = function() {
        const duration = Math.round(videoElement.duration);
        setVideoDuration(duration);
        uploadForm.setFieldsValue({
          duration: duration
        });
        window.URL.revokeObjectURL(videoElement.src);
      };
      
      videoElement.src = URL.createObjectURL(file);
      
      // 解锁其他表单字段
      uploadForm.setFieldsValue({
        grade_name: undefined,
        class_name: undefined,
        course_name: undefined,
        custom_tags: undefined
      });
    }
    
    return false; // 阻止自动上传
  };

  // 处理上传提交
  const handleUploadSubmit = async () => {
    // 定义上传消息key，确保在整个函数中可以访问
    const uploadMessageKey = 'upload-progress';
    
    try {
      const values = await uploadForm.validateFields();
      
      // 检查是否为微课模式
      if (!isMicroClass) {
        // 普通模式下需要选择历史课堂
        if (!selectedUploadClass) {
          message.error('请选择一个历史课堂');
          return;
        }
        
        // 检查选中的课堂是否有fileId（用于标记存档状态）
        if (!selectedUploadClass.fileId) {
          message.warning('选中的课堂缺少fileId信息，上传后可能无法正确标记存档状态');
        }
      }
      
      // 检查是否选择了文件
      if (!uploadFile) {
        message.error('请选择要上传的视频文件');
        return;
      }
      
      // 检查教师信息
      if (!selectedTeacher || !selectedTeacher.userId) {
        message.error('教师信息异常，请重新选择教师');
        return;
      }
      
      // 检查时长（仅微课模式）
      if (isMicroClass && (!values.duration || values.duration <= 0)) {
        message.error('请输入有效的视频时长');
        return;
      }
      
      setUploading(true);
      
      // 生成文件名：存档文件名+后六位随机数.mp4
      const randomSuffix = generateRandomSuffix();
      const filename = values.filename || '未命名课堂';
      const finalFilename = `${filename}_${randomSuffix}.mp4`;
      const uploadTaskId = `upload_task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // 创建FormData用于文件上传
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('filename', finalFilename);
      formData.append('task_id', uploadTaskId);
      
      // 显示初始上传提示
      message.loading({
        content: '准备上传...',
        key: uploadMessageKey,
        duration: 0
      });
      
      // 先上传到服务器暂存区，OSS转存由后端异步处理
      const uploadResponse = await axiosInstance.post('/plaso/upload-video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 600000, // 10分钟超时
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // 使用相同的key更新进度，避免创建多个提示
          message.loading({
            content: `上传中... ${percentCompleted}%`,
            key: uploadMessageKey,
            duration: 0
          });
        }
      });
      
      // 清除上传进度提示
      message.destroy(uploadMessageKey);
      
      if (uploadResponse.code === 0) {
        // 文件上传到服务器成功，先创建“进行中”的数据库记录
        const currentTime = moment().format('YYYY-MM-DD HH:mm:ss');
        
        let taskData;
        
        if (isMicroClass) {
          // 微课模式数据
          taskData = {
            teacher_id: selectedTeacher.userId,
            teacher_name: selectedTeacher.fullname,
            grade_name: values.grade_name,
            class_name: values.class_name,
            course_name: values.course_name,
            custom_tags: values.custom_tags || [],
            short_desc: filename,
            record_id: '', // 微课模式下record_id为空字符串
            file_id: `micro_class_${Date.now()}`, // 微课模式下生成唯一的file_id
            task_id: uploadTaskId,
            status: 4, // 转存进行中
            create_time: currentTime,
            finish_time: null,
            download_url: '',
            duration: values.duration || 0, // 使用用户输入的时长
            course_start_time: currentTime // 微课模式下上课时间为当前时间
          };
        } else {
          // 普通模式数据
          taskData = {
            teacher_id: selectedTeacher.userId,
            teacher_name: selectedTeacher.fullname,
            grade_name: values.grade_name,
            class_name: values.class_name,
            course_name: values.course_name,
            custom_tags: values.custom_tags || [],
            short_desc: filename,
            record_id: '', // 上传任务的record_id保持为空值
            file_id: selectedUploadClass.fileId || `upload_file_${Date.now()}`, // 使用实际的file_id或生成唯一值
            task_id: uploadTaskId,
            status: 4, // 转存进行中
            create_time: currentTime,
            finish_time: null,
            download_url: '',
            duration: selectedUploadClass.duration || 0,
            course_start_time: selectedUploadClass.createTime ? 
              moment(Number(selectedUploadClass.createTime)).format('YYYY-MM-DD HH:mm:ss') : null
          };
        }
        
        // 创建数据库记录
        const createResponse = await axiosInstance.post('/plaso/create-upload-task', taskData);
        
        if (createResponse.code === 0) {
          message.success('视频已上传到服务器，正在转存OSS，请稍后查看“已存档”状态');
          
          // 关闭对话框，重置表单
          setUploadModalVisible(false);
          uploadForm.resetFields();
          setSelectedUploadClass(null);
          setUploadFile(null);
          setIsMicroClass(false);
          setVideoDuration(0);
          
          // 刷新列表
          fetchDownloadTasks(pagination.current, pagination.pageSize);
          fetchTagOptions();
        } else {
          message.error(createResponse.message || '创建存档记录失败');
        }
      } else {
        message.error(uploadResponse.message || '文件上传失败');
      }
      
    } catch (error) {
      console.error('上传失败:', error);
      message.destroy(uploadMessageKey);

      if (error.message?.includes('最多2个同时上传') || error.message?.includes('4290')) {
        message.error('当前上传任务较多（最多2个同时上传），请稍后重试');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message.error('上传超时，请检查网络连接或文件大小');
      } else {
        message.error(`上传失败：${error.message || '未知错误'}`);
      }
    } finally {
      setUploading(false);
    }
  };

  // 获取状态图标
  const getStatusIcon = (status, download_url, record = null) => {
    // 如果有下载地址，直接显示已完成
    if (download_url) {
      return <CheckCircleOutlined style={{ fontSize: '18px', color: '#52c41a' }} />;
    }
    
    // 如果status是undefined或null，返回default
    if (status === undefined || status === null) {
      return <ExclamationCircleOutlined style={{ fontSize: '18px', color: '#d9d9d9' }} />;
    }
    
    // 将status转换为数字类型进行比较
    status = Number(status);
    
    if (status === 4) {
      if (record?.task_id && record.task_id.startsWith('upload_task_')) {
        return <CloudUploadOutlined style={{ fontSize: '18px', color: '#13c2c2' }} />; // 手动上传转存中
      }
      return <HourglassOutlined style={{ fontSize: '18px', color: '#faad14' }} />;  // 队列等待中
    }
    if (status >= 0 && status <= 3) return <CloudServerOutlined style={{ fontSize: '18px', color: '#1890ff' }} />;  // 下载中状态 - 使用云服务器图标
    if (status === 99) return <CheckCircleOutlined style={{ fontSize: '18px', color: '#52c41a' }} />;  // 成功 - 使用勾选图标更直观
    if (status < 0) return <ExclamationCircleOutlined style={{ fontSize: '18px', color: '#f5222d' }} />;  // 失败状态
    
    return <ExclamationCircleOutlined style={{ fontSize: '18px', color: '#d9d9d9' }} />;  // 其他状态
  };

  // 获取状态提示文本
  const getStatusTooltipText = (status, download_url, record = null) => {
    // 如果有下载地址，直接显示已存档
    if (download_url) {
      return '已存档';
    }
    return getStatusText(status, record);
  };

  // 获取审核状态图标
  const getReviewStatusIcon = (isReviewed) => {
    return isReviewed ? 
      <EyeOutlined style={{ fontSize: '18px', color: '#FBBC04' }} /> : 
      <EyeOutlined style={{ fontSize: '18px', color: '#d9d9d9' }} />;
  };

  // 获取下载接口类型图标
  const getDownloadTypeIcon = (record) => {
    // 上传任务：task_id以"upload_task_"开头且record_id为空
    if (record.task_id && record.task_id.startsWith('upload_task_') && 
        (!record.record_id || record.record_id === '')) {
      return <UploadOutlined style={{ fontSize: '18px', color: '#722ed1' }} />;
    }
    
    // 付费接口：record_id不为空
    // 免费接口：record_id为空但不是上传任务
    const isFreeApi = !record.record_id || record.record_id === '';
    
    return isFreeApi ? 
      <CodeOutlined style={{ fontSize: '18px', color: '#1890ff' }} /> : 
      <DollarOutlined style={{ fontSize: '18px', color: '#fa8c16' }} />;
  };

  // 获取下载接口类型提示文本
  const getDownloadTypeTooltip = (record) => {
    // 上传任务：task_id以"upload_task_"开头且record_id为空
    if (record.task_id && record.task_id.startsWith('upload_task_') && 
        (!record.record_id || record.record_id === '')) {
      return '手动上传';
    }
    
    const isFreeApi = !record.record_id || record.record_id === '';
    return isFreeApi ? '免费接口' : '付费接口';
  };

  // 获取状态文本
  const getStatusText = (status, record = null) => {
    // 如果status是undefined或null，返回未知状态
    if (status === undefined || status === null) {
      return '未知状态';
    }
    
    // 将status转换为数字类型进行比较
    status = Number(status);
    
    switch (status) {
      case 0:
        return '下载中';
      case 1:
        return '开始执行';
      case 2:
        return '开始上传';
      case 3:
        return '开始回调';
      case 4:
        if (record?.task_id && record.task_id.startsWith('upload_task_')) {
          return '转存中（已上传到服务器）';
        }
        return '等待中（任务队列已满）';
      case 99:
        return '成功';
      case -1:
        return '转码失败，等待二次回调';
      case -2:
        return '上传失败';
      case -3:
        return '回调失败';
      default:
        return `状态码(${status})`;  // 显示具体的状态码，方便调试
    }
  };

  // 获取状态颜色
  const getStatusColor = (status) => {
    // 如果status是undefined或null，返回default
    if (status === undefined || status === null) {
      return 'default';
    }
    
    // 将status转换为数字类型进行比较
    status = Number(status);
    
    if (status >= 0 && status <= 4) return 'processing';  // 执行中状态
    if (status === 99) return 'success';  // 成功
    if (status < 0) return 'error';  // 失败状态
    return 'default';  // 其他状态
  };

  // 获取下载状态文本
  const getDownloadStatusText = (status) => {
    // 如果status是undefined或null，记录日志并返回空字符串
    if (status === undefined || status === null) {
      return '';
    }
    
    // 将status转换为数字类型进行比较
    const numStatus = Number(status);
    
    // 状态为0、1、2、3、4显示为下载中
    if (numStatus >= 0 && numStatus <= 4) {
      return '下载中';
    }
    // 状态为99显示为下载成功
    if (numStatus === 99) {
      return '下载成功';
    }
    // 状态为-1、-2、-3显示为下载失败
    if (numStatus < 0) {
      return '下载失败';
    }
    
    return '';
  };

  // 获取下载状态颜色
  const getDownloadStatusColor = (status) => {
    // 如果status是undefined或null，记录日志并返回默认颜色
    if (status === undefined || status === null) {
      return 'default';
    }
    
    // 将status转换为数字类型进行比较
    const numStatus = Number(status);
    
    // 状态为0、1、2、3、4使用处理中颜色
    if (numStatus >= 0 && numStatus <= 4) {
      return 'processing';
    }
    // 状态为99使用成功颜色
    if (numStatus === 99) {
      return 'success';
    }
    // 状态为-1、-2、-3使用错误颜色
    if (numStatus < 0) {
      return 'error';
    }
    
    return 'default';
  };



  const handleDeleteTask = (id) => {
    modal.confirm({
      title: '确认删除',
      content: '确定要删除此存档任务吗？',
      onOk: async () => {
        message.loading('删除中...');
        try {
          const response = await deleteDownloadTask(id);
          if (response.code === 0) {
            // 检查是否有清理的标签信息
            let successMessage = '删除成功';
            if (response.data && response.data.cleaned_tags && response.data.cleaned_tags.length > 0) {
              successMessage += `，已清理未使用的标签：${response.data.cleaned_tags.join('、')}`;
            }
            message.success(successMessage);
            
            // 刷新主列表
            fetchDownloadTasks(pagination.current, pagination.pageSize);
            
            // 刷新下载管理器列表（如果抽屉是打开的）
            if (downloadTaskDrawerVisible) {
              fetchDownloadingTasks(downloadingTasksPagination.current, downloadingTasksPagination.pageSize);
            }
            
            fetchTagOptions(); // 重新获取标签列表，因为可能有标签被清理
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          console.error('删除任务出错:', error);
          message.error(error.message || '删除失败');
        }
      },
    });
  };



  // 计算耗时
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    
    // 转换为小时、分钟、秒
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    // 格式化显示
    let duration = '';
    if (hours > 0) duration += `${hours}小时`;
    if (minutes > 0) duration += `${minutes}分钟`;
    if (seconds > 0 || duration === '') duration += `${seconds}秒`;
    
    return duration;
  };

  // 上传用的历史课堂列表列定义（单选）
  const uploadHistoryClassesColumns = [
    {
      title: '选择',
      key: 'select',
      width: 60,
      render: (_, record) => (
        <Radio
          checked={selectedUploadClass?.recordId === record.recordId}
          onChange={() => handleUploadClassSelect(record)}
          disabled={isDisabledCheckbox(record)}
        />
      ),
    },
    {
      title: '课堂名称',
      dataIndex: 'shortDesc',
      key: 'shortDesc',
      ellipsis: true,
      render: (text, record) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tooltip title={text || '未命名课堂'}>
              <div style={{ 
                flex: 1, 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                maxWidth: '300px'
              }}>
                {text || '未命名课堂'}
              </div>
            </Tooltip>
            {record.has_download && (
              <Tag 
                color={getDownloadStatusColor(record.download_status)} 
                style={{ flexShrink: 0 }}
              >
                {getDownloadStatusText(record.download_status)}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: '课程时长',
      dataIndex: 'duration',
      key: 'duration',
      width: 180,
      render: (duration) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
          <span>{formatDuration(duration)}</span>
          {duration && duration < 600 && (
            <Tag 
              style={{ 
                backgroundColor: '#ff4d4f', 
                color: '#fff566',
                border: 'none'
              }}
            >
              测试课
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: '上课时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
      render: (createTime) => createTime ? moment(Number(createTime)).format('YYYY-MM-DD HH:mm:ss') : '-',
    }
  ];

  // 历史课堂列表列定义
  const historyClassesColumns = [
    {
      title: '课堂名称',
      dataIndex: 'shortDesc',
      key: 'shortDesc',
      ellipsis: true,
      render: (text, record) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tooltip title={text || '未命名课堂'}>
              <div style={{ 
                flex: 1, 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                maxWidth: '300px' // 限制最大宽度，确保状态图标有足够空间
              }}>
                {text || '未命名课堂'}
              </div>
            </Tooltip>
            {record.has_download && (
              <Tag 
                color={getDownloadStatusColor(record.download_status)} 
                style={{ flexShrink: 0 }} // 防止标签被压缩
              >
                {getDownloadStatusText(record.download_status)}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: '课程时长',
      dataIndex: 'duration',
      key: 'duration',
      width: 150,
      render: (duration) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
          <span>{formatDuration(duration)}</span>
          {duration && duration < 600 && ( // 10分钟 = 600秒
            <Tag 
              style={{ 
                backgroundColor: '#ff4d4f', 
                color: '#fff566',
                border: 'none'
              }}
            >
              测试课
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: '上课时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
      render: (createTime) => createTime ? moment(Number(createTime)).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '下课时间',
      dataIndex: 'createTime',
      key: 'endTime',
      width: 170,
      render: (createTime, record) => {
        if (!createTime || !record.duration) return '-';
        // 结束时间 = 开始时间 + 持续时间（毫秒）
        const endTime = Number(createTime) + (record.duration * 1000);
        return moment(endTime).format('YYYY-MM-DD HH:mm:ss');
      },
    }
  ];



  // 处理文件预览
  const handlePreviewFile = async (record) => {
    if (!record || !record.id) {
      message.error('无效的任务记录');
      return;
    }
    
    try {
      message.loading('获取临时预览链接中...');
      const response = await getSignedDownloadUrl(record.id);
      
      if (response.data && response.data.signed_url) {
        message.destroy(); // 清除加载提示
        window.open(response.data.signed_url, '_blank');
        message.success('正在打开预览...');
      } else {
        message.error('获取临时预览链接失败');
      }
    } catch (error) {
      console.error('获取带签名下载URL出错:', error);
      message.error('获取临时预览链接失败');
    }
  };

  // 处理文件下载
  const handleDownloadFile = async (record) => {
    if (!record || !record.id) {
      message.error('无效的任务记录');
      return;
    }

    if (!user?.id || !user?.username) {
      message.error('获取用户信息失败');
      return;
    }
    
    try {
      message.loading('获取临时下载链接中...');
      const response = await getSignedDownloadUrlForDownload(record.id);
      
      if (response.data && response.data.signed_url) {
        message.destroy();
        
        const link = document.createElement('a');
        link.href = response.data.signed_url;
        
        let fileName = record.short_desc || '未命名';
        if (!fileName.toLowerCase().endsWith('.mp4')) {
          fileName += '.mp4';
        }
        
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        try {
          await downloadLogsApi.createDownloadLog({
            task_id: record.id,
            task_name: record.short_desc || '未命名课堂',
            status: 1,
            user_id: user.id,
            user_name: user.username || user.fullname || '未知用户'
          });
        } catch (error) {
          console.error('记录下载日志失败:', error);
        }
        
        message.success('正在下载文件...');
      } else {
        message.error('获取临时下载链接失败');
        
        try {
          await downloadLogsApi.createDownloadLog({
            task_id: record.id,
            task_name: record.short_desc || '未命名课堂',
            status: 0,
            error_message: '获取临时下载链接失败',
            user_id: user.id,
            user_name: user.username || user.fullname || '未知用户'
          });
        } catch (error) {
          console.error('记录下载日志失败:', error);
        }
      }
    } catch (error) {
      console.error('获取带签名下载URL出错:', error);
      message.error('获取临时下载链接失败');
      
      try {
        await downloadLogsApi.createDownloadLog({
          task_id: record.id,
          task_name: record.short_desc || '未命名课堂',
          status: 0,
          error_message: error.message || '获取临时下载链接失败',
          user_id: user.id,
          user_name: user.username || user.fullname || '未知用户'
        });
      } catch (error) {
        console.error('记录下载日志失败:', error);
      }
    }
  };

  // 处理编辑按钮点击
  const handleEdit = (record) => {
    setCurrentEditRecord(record);
    editForm.setFieldsValue({
      grade_name: record.grade_name,
      class_name: record.class_name,
      course_name: record.course_name,
      short_desc: record.short_desc,
      is_reviewed: record.is_reviewed,
      custom_tags: record.custom_tags || []  // 设置当前任务的标签
    });
    setEditModalVisible(true);
    fetchTagOptions(); // 重新获取标签选项
  };

  // 处理编辑表单提交
  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      const response = await updateCourseArchiveInfo(currentEditRecord.id, {
        grade_name: values.grade_name,
        class_name: values.class_name,
        course_name: values.course_name,
        short_desc: values.short_desc,
        is_reviewed: values.is_reviewed,
        custom_tags: values.custom_tags || []  // 包含标签数据
      });
      
      // 更新成功，关闭对话框并更新本地数据
      setEditModalVisible(false);
      
      // 检查是否有清理的标签信息
      if (response.data && response.data.cleaned_tags && response.data.cleaned_tags.length > 0) {
        message.success(`更新成功，已清理未使用的标签：${response.data.cleaned_tags.join('、')}`);
      } else {
        message.success('更新成功');
      }
      
      // 检查是否修改了标签
      const tagsChanged = JSON.stringify(currentEditRecord.custom_tags || []) !== 
                        JSON.stringify(values.custom_tags || []);
      
      // 如果修改了标签且当前正在使用标签筛选，则清除筛选条件
      if (tagsChanged && filters.custom_tag) {
        setFilters(prev => ({
          ...prev,
          custom_tag: undefined
        }));
        // 重新获取列表数据
        fetchDownloadTasks(pagination.current, pagination.pageSize);
      } else {
        // 仅更新本地数据
        const newData = data.map(item => {
          if (item.id === currentEditRecord.id) {
            return {
              ...item,
              grade_name: values.grade_name,
              class_name: values.class_name,
              course_name: values.course_name,
              short_desc: values.short_desc,
              is_reviewed: values.is_reviewed,
              custom_tags: values.custom_tags || []  // 更新标签
            };
          }
          return item;
        });
        setData(newData);
      }
      
      fetchTagOptions(); // 重新获取标签列表，确保新创建的标签能显示
      
    } catch (error) {
      console.error('更新失败:', error);
      // 如果是业务逻辑错误（后端返回的错误消息），直接展示错误消息
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        // 如果是其他错误（如网络错误），显示通用错误消息
        message.error('更新失败，请稍后重试');
      }
    }
  };

  // 批量删除处理函数
  const handleBatchDelete = () => {
    if (selectedTaskIds.length === 0) {
      message.warning('请选择要删除的任务');
      return;
    }

    modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedTaskIds.length} 个任务吗？`,
      onOk: async () => {
        setBatchLoading(true);
        try {
          // 使用批量删除接口
          const response = await batchDeleteDownloadTasks(selectedTaskIds);
          
          if (response.code === 0) {
            const { deleted_count, cleaned_tags, oss_errors } = response.data || {};
            
            let successMessage = `成功删除 ${deleted_count || selectedTaskIds.length} 个任务`;
            if (cleaned_tags && cleaned_tags.length > 0) {
              successMessage += `，清理了 ${cleaned_tags.length} 个未使用的标签：${cleaned_tags.join(', ')}`;
            }
            if (oss_errors && oss_errors.length > 0) {
              successMessage += `，但有 ${oss_errors.length} 个文件删除失败`;
            }
            
            message.success(successMessage);
          } else {
            message.error(response.message || '批量删除失败');
          }
          
          setSelectedTaskIds([]);
          fetchDownloadTasks(pagination.current, pagination.pageSize);
          fetchTagOptions(); // 重新获取标签列表，因为可能有标签被清理
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败');
        } finally {
          setBatchLoading(false);
        }
      },
    });
  };

  // 批量编辑处理函数
  const handleBatchEdit = () => {
    if (selectedTaskIds.length === 0) {
      message.warning('请选择要编辑的任务');
      return;
    }
    
    // 重置表单和状态
    batchEditForm.resetFields();
    setClearTagsChecked(false);
    setBatchEditModalVisible(true);
    fetchTagOptions(); // 重新获取标签选项
  };

  // 批量编辑表单提交
  const handleBatchEditSubmit = async () => {
    try {
      const values = await batchEditForm.validateFields();
      
      // 检查是否至少选择了一个要更新的字段
      const hasChanges = values.grade_name || values.class_name || values.course_name || 
                        values.is_reviewed !== undefined || (values.custom_tags && values.custom_tags.length > 0) ||
                        values.clear_tags === true;
      
      if (!hasChanges) {
        message.warning('请至少修改一个字段');
        return;
      }
      
      setBatchLoading(true);
      
      // 构建更新数据，只包含有值的字段
      const updateData = {};
      if (values.grade_name) updateData.grade_name = values.grade_name;
      if (values.class_name) updateData.class_name = values.class_name;
      if (values.course_name) updateData.course_name = values.course_name;
      if (values.is_reviewed !== undefined) updateData.is_reviewed = values.is_reviewed;
      
      // 处理标签逻辑：如果选择了清空标签，则设置为空数组；否则使用选择的标签
      if (values.clear_tags === true) {
        updateData.custom_tags = [];  // 清空标签
      } else if (values.custom_tags) {
        updateData.custom_tags = values.custom_tags;  // 使用选择的标签
      }
      
      // 批量更新所有选中的任务
      const updatePromises = selectedTaskIds.map(taskId => 
        updateCourseArchiveInfo(taskId, updateData)
      );
      
      const updateResults = await Promise.all(updatePromises);
      
      // 收集所有清理的标签
      const allCleanedTags = new Set();
      updateResults.forEach(result => {
        if (result.data && result.data.cleaned_tags) {
          result.data.cleaned_tags.forEach(tag => allCleanedTags.add(tag));
        }
      });
      
      // 构建成功消息
      let successMessage = `成功批量编辑 ${selectedTaskIds.length} 个任务`;
      if (values.clear_tags === true) {
        successMessage += '，已清空所有标签';
      } else if (values.custom_tags && values.custom_tags.length > 0) {
        successMessage += `，已设置标签：${values.custom_tags.join('、')}`;
      }
      
      if (allCleanedTags.size > 0) {
        successMessage += `，已清理未使用的标签：${Array.from(allCleanedTags).join('、')}`;
      }
      
      message.success(successMessage);
      setBatchEditModalVisible(false);
      setSelectedTaskIds([]);
      setClearTagsChecked(false);  // 重置状态
      
      // 如果有标签相关操作，先重置筛选条件，确保不会使用旧的标签筛选
      if (values.clear_tags === true || values.custom_tags) {
        // 如果当前正在使用标签筛选，则清除筛选条件
        if (filters.custom_tag) {
          setFilters(prev => ({
            ...prev,
            custom_tag: undefined
          }));
        }
      }
      
      fetchDownloadTasks(pagination.current, pagination.pageSize);
      fetchTagOptions(); // 重新获取标签列表，确保新创建的标签能显示
      
    } catch (error) {
      console.error('批量编辑失败:', error);
      message.error('批量编辑失败，请稍后重试');
    } finally {
      setBatchLoading(false);
    }
  };

  // 设置下载列表列定义
  const columns = [
    {
      title: '状态',
      key: 'status',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <Tag 
            size="small" 
            color="default"
            style={{ 
              fontSize: '10px', 
              padding: '0 4px',
              lineHeight: '16px',
              color: '#999',
              backgroundColor: '#f5f5f5',
              border: '1px solid #e8e8e8'
            }}
          >
            #{record.id}
          </Tag>
          <Tooltip title={getStatusTooltipText(record.status, record.download_url, record)}>
            {getStatusIcon(record.status, record.download_url, record)}
          </Tooltip>
          <Tooltip title={record.is_reviewed ? '已审核' : '未审核'}>
            {getReviewStatusIcon(record.is_reviewed)}
          </Tooltip>
          <Tooltip title={getDownloadTypeTooltip(record)}>
            {getDownloadTypeIcon(record)}
          </Tooltip>
        </div>
      ),
    },
    {
      title: '任务信息',
      key: 'task_info',
      render: (_, record) => (
        <div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '400px' }}>
            <strong>课堂：</strong>{record.short_desc}
          </div>
          <div><strong>老师：</strong>{record.teacher_name}</div>
          <div>
            <strong>课程时长：</strong>{formatDuration(record.duration || 0)}
            {record.duration && record.duration < 600 && (
              <Tag 
                style={{ 
                  backgroundColor: '#ff4d4f', 
                  color: '#fff566',
                  border: 'none',
                  marginLeft: '8px'
                }}
              >
                测试课
              </Tag>
            )}
          </div>
          {record.custom_tags && record.custom_tags.length > 0 && (
            <div style={{ marginTop: '4px' }}>
              {record.custom_tags.map((tag, index) => (
                <Tag key={index} color="green" size="small" style={{ marginBottom: '2px', marginRight: '4px' }}>
                  {tag}
                </Tag>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '届次',
      dataIndex: 'grade_name',
      key: 'grade_name',
    },
    {
      title: '班次',
      dataIndex: 'class_name',
      key: 'class_name',
    },
    {
      title: '课程',
      dataIndex: 'course_name',
      key: 'course_name',
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>时间</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Button
              type="text"
              size="small"
              style={{ 
                height: '16px', 
                width: '60px', 
                fontSize: '10px', 
                padding: '0 4px',
                backgroundColor: sortOrder.startsWith('course_time') ? '#E5F2F1' : 'transparent',
                color: sortOrder.startsWith('course_time') ? '#284D40' : '#666',
                border: sortOrder.startsWith('course_time') ? '1px solid #284D40' : '1px solid #d9d9d9'
              }}
              onClick={() => {
                const newOrder = sortOrder === 'course_time_desc' ? 'course_time_asc' : 'course_time_desc';
                setSortOrder(newOrder);
                setPagination(prev => ({ ...prev, current: 1 })); // 重置到第一页
              }}
            >
              上课时间
              {sortOrder === 'course_time_desc' && ' ↓'}
              {sortOrder === 'course_time_asc' && ' ↑'}
            </Button>
            <Button
              type="text"
              size="small"
              style={{ 
                height: '16px', 
                width: '60px', 
                fontSize: '10px', 
                padding: '0 4px',
                backgroundColor: sortOrder.startsWith('create_time') ? '#E5F2F1' : 'transparent',
                color: sortOrder.startsWith('create_time') ? '#284D40' : '#666',
                border: sortOrder.startsWith('create_time') ? '1px solid #284D40' : '1px solid #d9d9d9'
              }}
              onClick={() => {
                const newOrder = sortOrder === 'create_time_desc' ? 'create_time_asc' : 'create_time_desc';
                setSortOrder(newOrder);
                setPagination(prev => ({ ...prev, current: 1 })); // 重置到第一页
              }}
            >
              存档时间
              {sortOrder === 'create_time_desc' && ' ↓'}
              {sortOrder === 'create_time_asc' && ' ↑'}
            </Button>
            <Button
              type="text"
              size="small"
              style={{ 
                height: '16px', 
                width: '60px', 
                fontSize: '10px', 
                padding: '0 4px',
                backgroundColor: sortOrder.startsWith('finish_time') ? '#E5F2F1' : 'transparent',
                color: sortOrder.startsWith('finish_time') ? '#284D40' : '#666',
                border: sortOrder.startsWith('finish_time') ? '1px solid #284D40' : '1px solid #d9d9d9'
              }}
              onClick={() => {
                const newOrder = sortOrder === 'finish_time_desc' ? 'finish_time_asc' : 'finish_time_desc';
                setSortOrder(newOrder);
                setPagination(prev => ({ ...prev, current: 1 })); // 重置到第一页
              }}
            >
              完成时间
              {sortOrder === 'finish_time_desc' && ' ↓'}
              {sortOrder === 'finish_time_asc' && ' ↑'}
            </Button>
          </div>
        </div>
      ),
      key: 'time',
      width: 220,
      render: (_, record) => {
        const duration = calculateDuration(record.create_time, record.finish_time);
        
        return (
          <div style={{ fontSize: '12px', color: '#666' }}>
            {/* 课程开始时间（上课时间） */}
            {record.course_start_time && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <PlayCircleOutlined style={{ color: '#52c41a' }} />
                <span style={{ 
                  fontWeight: sortOrder.startsWith('course_time') ? 'bold' : 'normal',
                  color: sortOrder.startsWith('course_time') ? '#284D40' : '#666'
                }}>
                  上课: {record.course_start_time}
                </span>
              </div>
            )}
            
            {/* 任务时间信息 */}
            <div style={{ 
              borderTop: record.course_start_time ? '1px dashed #d9d9d9' : 'none',
              paddingTop: record.course_start_time ? '4px' : '0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ClockCircleOutlined style={{ color: '#284D40' }} />
                <span style={{ 
                  fontWeight: sortOrder.startsWith('create_time') ? 'bold' : 'normal',
                  color: sortOrder.startsWith('create_time') ? '#284D40' : '#666'
                }}>
                  存档: {record.create_time}
                </span>
              </div>
              {record.finish_time && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span style={{ 
                    fontWeight: sortOrder.startsWith('finish_time') ? 'bold' : 'normal',
                    color: sortOrder.startsWith('finish_time') ? '#284D40' : '#666'
                  }}>
                    完成: {record.finish_time}
                  </span>
                </div>
              )}
              {duration && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                  <FieldTimeOutlined style={{ color: '#faad14' }} />
                  <span>耗时: {duration}</span>
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: '创建人',
      dataIndex: 'operator_name',
      key: 'operator_name',
      width: 100,
      render: (text) => text || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => {
        // 将status转换为数字类型
        const status = Number(record.status);
        
        // 如果状态是0-4（下载中、开始执行、开始上传、开始回调），显示不可操作提示
        if (status >= 0 && status <= 4) {
          return (
            <div style={{ color: '#999', fontSize: '12px' }}>
              任务进行中<br/>不可操作
            </div>
          );
        }
        
        // 如果状态是99（成功），显示所有操作按钮
        if (status === 99) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                <Button 
                  type="link" 
                  size="small" 
                  icon={<EyeOutlined />}
                  onClick={() => handlePreviewFile(record)}
                >
                  预览
                </Button>
                <Button 
                  type="link" 
                  size="small" 
                  icon={<CloudDownloadOutlined />}
                  onClick={() => handleDownloadFile(record)}
                >
                  下载
                </Button>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <Button 
                  type="link" 
                  size="small" 
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                >
                  编辑
                </Button>
                <Button 
                  type="link" 
                  size="small" 
                  danger 
                  onClick={() => handleDeleteTask(record.id)}
                  icon={<DeleteOutlined />}
                >
                  删除
                </Button>
              </div>
            </div>
          );
        }
        
        // 如果状态是-1、-2、-3（失败状态），只显示删除按钮
        if (status < 0) {
          return (
            <Button 
              type="link" 
              size="small" 
              danger 
              onClick={() => handleDeleteTask(record.id)}
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          );
        }
        
        // 其他状态不显示任何按钮
        return null;
      },
    },
  ];

  // 下载中任务列表列定义
  const downloadingTasksColumns = [
    {
      title: '状态',
      key: 'status',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <Tag 
            size="small" 
            color="default"
            style={{ 
              fontSize: '10px', 
              padding: '0 4px',
              lineHeight: '16px',
              color: '#999',
              backgroundColor: '#f5f5f5',
              border: '1px solid #e8e8e8'
            }}
          >
            #{record.id}
          </Tag>
          <Tooltip title={getStatusTooltipText(record.status, record.download_url, record)}>
            {getStatusIcon(record.status, record.download_url, record)}
          </Tooltip>
          <Tooltip title={getDownloadTypeTooltip(record)}>
            {getDownloadTypeIcon(record)}
          </Tooltip>
        </div>
      ),
    },
    {
      title: '任务信息',
      key: 'task_info',
      render: (_, record) => (
        <div>
          <div style={{ 
            fontWeight: 'bold',
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            maxWidth: '250px',
            marginBottom: '4px'
          }}>
            {record.short_desc}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
            {record.teacher_name} · {record.grade_name} · {record.class_name}
          </div>
          {/* 添加上课时间信息 */}
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px', display: 'flex', alignItems: 'center' }}>
            <FieldTimeOutlined style={{ fontSize: '10px', marginRight: '4px' }} />
            {record.course_start_time ? `上课: ${record.course_start_time}` : '无上课时间'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>
              时长: {formatDuration(record.duration || 0)}
            </span>
            <Tag 
              color={!record.record_id || record.record_id === '' ? 'green' : 'orange'}
              size="small"
              style={{ fontSize: '10px', margin: 0 }}
            >
              {!record.record_id || record.record_id === '' ? '免费接口' : '收费接口'}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: '存档时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 120,
      render: (text) => (
        <div style={{ fontSize: '12px' }}>
          {text}
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const status = Number(record.status);
        
        // 只有状态为4（等待中）的任务可以删除
        if (status === 4) {
          return (
            <Button 
              type="link" 
              size="small" 
              danger 
              onClick={() => handleDeleteTask(record.id)}
              icon={<DeleteOutlined />}
              style={{ fontSize: '12px' }}
            >
              删除
            </Button>
          );
        }
        
        // 其他状态不显示操作按钮
        return <span style={{ color: '#999', fontSize: '12px' }}>-</span>;
      },
    },
  ];

  // 处理下载任务抽屉打开
  const handleDownloadTaskDrawerOpen = () => {
    setDownloadTaskDrawerVisible(true);
    fetchDownloadingTasks(1, 20); // 刷新下载中任务列表，显示第一页
  };

  // 处理下载中任务分页变化
  const handleDownloadingTasksPaginationChange = (page, pageSize) => {
    fetchDownloadingTasks(page, pageSize);
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={showDownloadModal}>
                新建存档任务
              </Button>
              <Button type="primary" icon={<UploadOutlined />} onClick={showUploadModal}>
                上传视频
              </Button>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleManualRefresh}
                loading={loading}
              >
                刷新
              </Button>
            </Space>
            
            <Space wrap>
              <Form layout="inline">
              <Form.Item label="状态">
                  <Select
                    style={{ width: 100 }}
                    placeholder="选择状态"
                    value={filters.status}
                    onChange={(value) => handleFilterChange({ status: value })}
                    allowClear
                  >
                    {statusOptions.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="届次">
                  <Select
                    style={{ width: 100 }}
                    placeholder="选择届次"
                    value={filters.grade}
                    onChange={(value) => handleFilterChange({ grade: value })}
                    allowClear
                  >
                    {gradeOptions.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="班次">
                  <Select
                    style={{ width: 100 }}
                    placeholder="选择班次"
                    value={filters.classType}
                    onChange={(value) => handleFilterChange({ classType: value })}
                    allowClear
                  >
                    {classOptions.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="课程">
                                      <Select
                      style={{ width: 120 }}
                      placeholder="选择课程"
                      value={filters.course}
                      onChange={(value) => handleFilterChange({ course: value })}
                      allowClear
                      listHeight={200}
                      showSearch
                      optionFilterProp="label"
                    >
                    {courseOptions.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="标签">
                  <Select
                    style={{ width: 120 }}
                    placeholder="选择标签"
                    value={filters.custom_tag}
                    onChange={(value) => handleFilterChange({ custom_tag: value })}
                    allowClear
                    loading={tagLoading}
                    listHeight={200}
                  >
                    {tagOptions.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Input.Search
                    placeholder="搜索名称、老师等"
                    allowClear
                    onSearch={handleSearch}
                    style={{ width: 180 }}
                  />
                </Form.Item>
                <Form.Item>
                  <Button onClick={handleReset}>重置</Button>
                </Form.Item>
              </Form>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={false}
            loading={loading}
            onChange={handleTableChange}
            rowSelection={mainTableRowSelection}
            locale={{
              emptyText: <Empty description="暂无存档任务" />
            }}
          />
          
          {data.length > 0 && (
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={(page, pageSize) => {
                  // 强制使用用户选择的页码
                  const userPage = page;
                  const userPageSize = pageSize || pagination.pageSize;
                  
                  // 确保页码有效
                  if (userPage < 1) return;
                  
                  setPagination(prev => ({
                    ...prev,
                    current: userPage,
                    pageSize: userPageSize
                  }));
                  
                  // 状态更新会触发useEffect，它会调用fetchDownloadTasks
                }}
                showSizeChanger
                showQuickJumper
                showTotal={(total) => `共 ${total} 条`}
              />
            </div>
          )}

          {/* 批量操作栏 */}
          {selectedTaskIds.length > 0 && (
            <div style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              padding: '12px 24px',
              background: '#fff',
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px'
            }}>
              <span>已选择 {selectedTaskIds.length} 项</span>
              <Button 
                type="primary"
                icon={<EditOutlined />} 
                onClick={handleBatchEdit}
                loading={batchLoading}
              >
                批量编辑
              </Button>
              <Button 
                danger 
                icon={<DeleteOutlined />} 
                onClick={handleBatchDelete}
                loading={batchLoading}
              >
                批量删除
              </Button>
              <Button 
                onClick={() => setSelectedTaskIds([])}
              >
                取消选择
              </Button>
            </div>
          )}
        </Space>
      </Card>

      {/* 右下角的下载任务圆形图标 */}
      <div
        style={{
          position: 'fixed',
          bottom: selectedTaskIds.length > 0 ? '80px' : '24px', // 如果有批量操作栏则往上移
          right: '24px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: '#00695C',
          boxShadow: '0 4px 12px rgba(73, 202, 142, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 1001,
          transition: 'all 0.3s ease'
        }}
        onClick={handleDownloadTaskDrawerOpen}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(73, 202, 142, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(73, 202, 142, 0.4)';
        }}
      >
        <DownloadOutlined style={{ fontSize: '24px', color: '#fff' }} />
                 {downloadingTasksPagination.total > 0 && (
           <div
             style={{
               position: 'absolute',
               top: '-6px',
               right: '-6px',
               width: '25px',
               height: '25px',
               borderRadius: '50%',
               backgroundColor: '#ff4d4f',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               fontSize: '10px',
               color: '#fff',
               fontWeight: 'bold',
               border: '2px solid #fff'
             }}
           >
             {downloadingTasksPagination.total > 99 ? '99+' : downloadingTasksPagination.total}
           </div>
         )}
      </div>

      {/* 下载任务抽屉 */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DownloadOutlined />
                         <span>下载管理器</span>
             <span style={{ fontSize: '14px', color: '#666' }}>
               ({downloadingTasksPagination.total} 个任务)
             </span>
          </div>
        }
        placement="right"
        width={600}
        onClose={() => setDownloadTaskDrawerVisible(false)}
        open={downloadTaskDrawerVisible}
                 extra={
           <Button 
             icon={<ReloadOutlined />} 
             size="small"
             onClick={() => fetchDownloadingTasks(downloadingTasksPagination.current, downloadingTasksPagination.pageSize)}
             loading={downloadingTasksLoading}
           >
             刷新
           </Button>
         }
      >
        <div>
          {downloadingTasks.length > 0 ? (
            <div>
                             <Table
                 columns={downloadingTasksColumns}
                 dataSource={downloadingTasks}
                 rowKey="id"
                 pagination={false}
                 loading={downloadingTasksLoading}
                 size="small"
                 locale={{
                   emptyText: <Empty description="暂无下载中任务" />
                 }}
               />
               
               {downloadingTasksPagination.total > downloadingTasksPagination.pageSize && (
                 <div style={{ marginTop: '16px', textAlign: 'center' }}>
                   <Pagination
                     current={downloadingTasksPagination.current}
                     pageSize={downloadingTasksPagination.pageSize}
                     total={downloadingTasksPagination.total}
                     onChange={handleDownloadingTasksPaginationChange}
                     showSizeChanger={false}
                     showQuickJumper={false}
                     showTotal={(total, range) => `${range[0]}-${range[1]} / 共 ${total} 个任务`}
                     size="small"
                   />
                 </div>
               )}
            </div>
          ) : (
            <Empty 
              description="暂无下载中任务" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </div>
      </Drawer>
      
      {/* 远程存档任务对话框 */}
      <Modal
        title="新建存档任务"
        open={downloadModalVisible}
        onCancel={() => {
          setDownloadModalVisible(false);
          setDownloadModeModalVisible(false);
          setPendingSubmitData(null);
        }}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setDownloadModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleDownloadSubmit} 
            disabled={!selectedHistoryClasses.length || !gradeNameValue || !classNameValue || !courseNameValue || !teacherIdValue || getDuplicateFilenames().size > 0}
          >
            {`提交 (${selectedHistoryClasses.length} 项)`}
          </Button>
        ]}
      >
        <Form
          form={downloadForm}
          layout="vertical"
        >
          <Form.Item
            name="teacher_id"
            label="选择教师"
            rules={[{ required: true, message: '请选择教师' }]}
          >
            <div style={{ position: 'relative' }}>
              <Select
                placeholder="请选择教师"
                loading={teacherListLoading}
                onChange={handleTeacherChange}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                listHeight={400}
                popupClassName="teacher-select-dropdown"
                filterOption={(input, option) => {
                  const teacher = teacherOptions.find(t => t.value === option.value);
                  if (!teacher) return false;
                  const searchText = input.toLowerCase();
                  return (teacher.name && teacher.name.toLowerCase().includes(searchText)) ||
                         (teacher.loginName && teacher.loginName.toLowerCase().includes(searchText));
                }}
                dropdownRender={(menu) => (
                  <div style={{ position: 'relative' }}>
                    {menu}
                    <div className="alphabet-index">
                      {(() => {
                        // 使用完整的字母表，包括中文拼音首字母
                        const fullAlphabet = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'W', 'X', 'Y', 'Z', '#'];
                        // 获取实际存在的首字母
                        const existingLetters = new Set(teacherOptions.map(t => t.firstLetter));
                        
                        return fullAlphabet.map(letter => (
                          <div 
                            key={letter} 
                            className={`letter ${existingLetters.has(letter) ? 'active' : ''}`}
                          >
                            {letter}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              >
                {(() => {
                  // 按首字母分组教师
                  const groupedTeachers = {};
                  teacherOptions.forEach(teacher => {
                    const letter = teacher.firstLetter || '#';
                    if (!groupedTeachers[letter]) {
                      groupedTeachers[letter] = [];
                    }
                    groupedTeachers[letter].push(teacher);
                  });
                  
                  // 按字母顺序排序
                  const sortedLetters = Object.keys(groupedTeachers).sort();
                  
                  return sortedLetters.map(letter => (
                    <Select.OptGroup 
                      key={`group-${letter}`}
                      label={
                        <div style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#284D40',
                          padding: '4px 0',
                          borderBottom: '1px solid #f0f0f0',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                        }}>
                          {letter}
                        </div>
                      }
                    >
                      {groupedTeachers[letter].map(teacher => (
                        <Select.Option key={teacher.value} value={teacher.value}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            width: '100%',
                            paddingRight: '30px' // 为右侧字母索引留出空间
                          }}>
                            <span style={{ 
                              fontWeight: '500',
                              color: teacher.is_deleted ? '#ff7875' : '#262626',
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                            }}>
                              {teacher.name || '未知姓名'}
                              
                            </span>
                            {teacher.loginName && !teacher.is_deleted && (
                              <Tag 
                                color="Green" 
                                size="small"
                                style={{ 
                                  margin: 0,
                                  fontSize: '11px',
                                  borderRadius: '10px',
                                  padding: '0 6px',
                                  lineHeight: '18px',
                                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                                }}
                              >
                                {teacher.loginName}
                              </Tag>
                            )}
                            {teacher.is_deleted && (
                              <Tag 
                                color="red" 
                                size="small"
                                style={{ 
                                  margin: 0,
                                  fontSize: '11px',
                                  borderRadius: '10px',
                                  padding: '0 6px',
                                  lineHeight: '18px',
                                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                                }}
                              >
                                已删除
                              </Tag>
                            )}
                          </div>
                        </Select.Option>
                      ))}
                    </Select.OptGroup>
                  ));
                })()}
              </Select>
            </div>
          </Form.Item>
          
          {selectedTeacherId && (
            <>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div>
                    <span>历史课堂列表</span>
                    <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
                      (单次最多可选择20个课堂)
                    </span>
                  </div>
                  {selectedHistoryClasses.length > 0 && (
                    <div>
                      <Space>
                        <span style={{ color: selectedHistoryClasses.length >= 20 ? '#ff4d4f' : 'inherit' }}>
                          已选择 {selectedHistoryClasses.length}/20 项，总时长: {formatDuration(getTotalDuration())}
                        </span>
                        <Button 
                          size="small" 
                          onClick={() => {
                            setSelectedRowKeys([]);
                            setSelectedHistoryClasses([]);
                          }}
                        >
                          清空选择
                        </Button>
                      </Space>
                    </div>
                  )}
                </div>
                
                <Spin spinning={historyClassesLoading}>
                  {historyClasses.length > 0 ? (
                    <>
                      <Table
                        rowSelection={historyClassesRowSelection}
                        columns={historyClassesColumns}
                        dataSource={historyClasses}
                        rowKey={(record) => record.recordId}
                        pagination={false}
                        size="small"
                        scroll={{ y: 300 }}
                      />
                      
                      <div style={{ marginTop: '16px', textAlign: 'right' }}>
                        <Pagination
                          size="small"
                          current={historyClassesPagination.current}
                          pageSize={historyClassesPagination.pageSize}
                          total={historyClassesPagination.total}
                          onChange={handleHistoryClassesPaginationChange}
                          showSizeChanger={false}
                          showTotal={(total) => `共 ${total} 条`}
                        />
                      </div>
                      
                      {selectedHistoryClasses.length > 0 && (
                        <div style={{ marginTop: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <Title level={5} style={{ margin: 0 }}>已选课堂文件名设置</Title>
                            {getDuplicateFilenames().size > 0 && (
                              <div style={{ 
                                color: '#ff4d4f', 
                                fontSize: '12px',
                                backgroundColor: '#fff2f0',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid #ffccc7'
                              }}>
                                ⚠️ 存在重复文件名：{Array.from(getDuplicateFilenames()).join('、')}
                              </div>
                            )}
                          </div>
                          <Table
                            dataSource={selectedHistoryClasses}
                            rowKey="recordId"
                            pagination={false}
                            size="small"
                            scroll={{ y: 200 }}
                            columns={[
                              {
                                title: '课堂名称',
                                dataIndex: 'short_Desc',
                                key: 'short_Desc',
                                ellipsis: true,
                                width: '40%',
                              },
                              {
                                title: '存档文件名',
                                key: 'filename',
                                width: '60%',
                                render: (_, record) => {
                                  const currentFilename = record.filename || record.short_Desc || '';
                                  const isDuplicate = isFilenameDuplicate(currentFilename);
                                  
                                  return (
                                    <FilenameInput
                                      record={record}
                                      isDuplicate={isDuplicate}
                                      onFilenameChange={handleFilenameChange}
                                    />
                                  );
                                },
                              },
                            ]}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <Empty description="暂无历史课堂" />
                  )}
                </Spin>
              </div>
              
              <div style={{ marginTop: '24px' }}>
                <Title level={5}>课堂存档信息</Title>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <Form.Item
                    name="grade_name"
                    label="届次"
                    rules={[{ required: true, message: '请选择届次' }]}
                    style={{ flex: 1, minWidth: '200px' }}
                  >
                    <Select
                      placeholder="请选择届次"
                      options={gradeOptions}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="class_name"
                    label="班次"
                    rules={[{ required: true, message: '请选择班次' }]}
                    style={{ flex: 1, minWidth: '200px' }}
                  >
                    <Select
                      placeholder="请选择班次"
                      options={classOptions}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="course_name"
                    label="课程"
                    rules={[{ required: true, message: '请选择课程' }]}
                    style={{ flex: 1, minWidth: '200px' }}
                  >
                    <Select
                      placeholder="请选择课程"
                      options={courseOptions}
                      style={{ width: '100%' }}
                      listHeight={200}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>

                  <Form.Item
                    name="custom_tags"
                    label="标签"
                    style={{ flex: 1, minWidth: '200px' }}
                  >
                    <Select
                      mode="tags"
                      style={{ width: '100%' }}
                      placeholder="请选择或直接输入创建新标签"
                      options={tagOptions}
                      loading={tagLoading}
                      allowClear
                      maxTagCount={5}
                      listHeight={200}
                    />
                  </Form.Item>
                </div>
              </div>
            </>
          )}
        </Form>
            </Modal>

      {/* 上传视频对话框 */}
      <Modal
        title="上传视频"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setSelectedUploadClass(null);
          setUploadFile(null);
          setIsMicroClass(false);
          setVideoDuration(0);
        }}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setUploadModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            icon={<UploadOutlined />}
            onClick={handleUploadSubmit} 
            loading={uploading}
            disabled={
              isMicroClass ? 
                (!uploadFile || !uploadGradeNameValue || !uploadClassNameValue || 
                !uploadCourseNameValue || !uploadTeacherIdValue || !uploadFilenameValue) : 
                (!selectedUploadClass || !uploadFile || !uploadGradeNameValue || 
                !uploadClassNameValue || !uploadCourseNameValue || !uploadTeacherIdValue || 
                !uploadFilenameValue)
            }
          >
            {uploading ? '上传中...' : '上传'}
          </Button>
        ]}
      >
        <Form
          form={uploadForm}
          layout="vertical"
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <Form.Item
              name="teacher_id"
              label="选择教师"
              rules={[{ required: true, message: '请选择教师' }]}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <div style={{ position: 'relative' }}>
                <Select
                  placeholder="请选择教师"
                  loading={teacherListLoading}
                  onChange={handleUploadTeacherChange}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  listHeight={400}
                  popupClassName="teacher-select-dropdown"
                  filterOption={(input, option) => {
                    const teacher = teacherOptions.find(t => t.value === option.value);
                    if (!teacher) return false;
                    const searchText = input.toLowerCase();
                    return (teacher.name && teacher.name.toLowerCase().includes(searchText)) ||
                          (teacher.loginName && teacher.loginName.toLowerCase().includes(searchText));
                  }}
                  dropdownRender={(menu) => (
                    <div style={{ position: 'relative' }}>
                      {menu}
                      <div className="alphabet-index">
                        {(() => {
                          const fullAlphabet = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'W', 'X', 'Y', 'Z', '#'];
                          const existingLetters = new Set(teacherOptions.map(t => t.firstLetter));
                          
                          return fullAlphabet.map(letter => (
                            <div 
                              key={letter} 
                              className={`letter ${existingLetters.has(letter) ? 'active' : ''}`}
                            >
                              {letter}
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                >
                  {(() => {
                    const groupedTeachers = {};
                    teacherOptions.forEach(teacher => {
                      const letter = teacher.firstLetter || '#';
                      if (!groupedTeachers[letter]) {
                        groupedTeachers[letter] = [];
                      }
                      groupedTeachers[letter].push(teacher);
                    });
                    
                    const sortedLetters = Object.keys(groupedTeachers).sort();
                    
                    return sortedLetters.map(letter => (
                      <Select.OptGroup 
                        key={`group-${letter}`}
                        label={
                          <div style={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: '#284D40',
                            padding: '4px 0',
                            borderBottom: '1px solid #f0f0f0',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                          }}>
                            {letter}
                          </div>
                        }
                      >
                        {groupedTeachers[letter].map(teacher => (
                          <Select.Option key={teacher.value} value={teacher.value}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              width: '100%',
                              paddingRight: '30px'
                            }}>
                              <span style={{ 
                                fontWeight: '500',
                                color: teacher.is_deleted ? '#ff7875' : '#262626',
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                              }}>
                                {teacher.name || '未知姓名'}
                                
                              </span>
                              {teacher.loginName && !teacher.is_deleted && (
                                <Tag 
                                  color="Green" 
                                  size="small"
                                  style={{ 
                                    margin: 0,
                                    fontSize: '11px',
                                    borderRadius: '10px',
                                    padding: '0 6px',
                                    lineHeight: '18px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                                  }}
                                >
                                  {teacher.loginName}
                                </Tag>
                              )}
                              {teacher.is_deleted && (
                                <Tag 
                                  color="red" 
                                  size="small"
                                  style={{ 
                                    margin: 0,
                                    fontSize: '11px',
                                    borderRadius: '10px',
                                    padding: '0 6px',
                                    lineHeight: '18px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                                  }}
                                >
                                  已删除
                                </Tag>
                              )}
                            </div>
                          </Select.Option>
                        ))}
                      </Select.OptGroup>
                    ));
                  })()}
                </Select>
              </div>
            </Form.Item>
            
            <Form.Item style={{ marginLeft: '16px', marginBottom: 0, marginTop: '26px' }}>
              <div style={{ display: 'flex', alignItems: 'center', height: '32px' }}>
                <Checkbox 
                  checked={isMicroClass} 
                  onChange={(e) => {
                    setIsMicroClass(e.target.checked);
                    if (e.target.checked) {
                      // 清空已选择的历史课堂
                      setSelectedUploadClass(null);
                    }
                  }}
                  disabled={!selectedTeacherId}
                >
                  上传微课
                </Checkbox>
                <Tooltip title="勾选后不需要选择历史课堂，可直接上传视频文件">
                  <QuestionCircleOutlined style={{ marginLeft: '4px', color: '#999' }} />
                </Tooltip>
              </div>
            </Form.Item>
          </div>
          
          {selectedTeacherId && (
            <>
              {!isMicroClass ? (
                // 普通模式显示历史课堂列表
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span>历史课堂列表（单选）</span>
                    {selectedUploadClass && (
                      <div>
                        <span style={{ color: '#284D40' }}>
                          已选择: 《{selectedUploadClass.shortDesc || '未命名课堂'}》，该课程将会被标记为"下载成功"
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <Spin spinning={historyClassesLoading}>
                    {historyClasses.length > 0 ? (
                      <>
                        <Table
                          columns={uploadHistoryClassesColumns}
                          dataSource={historyClasses}
                          rowKey={(record) => record.recordId}
                          pagination={false}
                          size="small"
                          scroll={{ y: 300 }}
                        />
                        
                        <div style={{ marginTop: '16px', textAlign: 'right' }}>
                          <Pagination
                            size="small"
                            current={historyClassesPagination.current}
                            pageSize={historyClassesPagination.pageSize}
                            total={historyClassesPagination.total}
                            onChange={handleHistoryClassesPaginationChange}
                            showSizeChanger={false}
                            showTotal={(total) => `共 ${total} 条`}
                          />
                        </div>
                      </>
                    ) : (
                      <Empty description="暂无历史课堂" />
                    )}
                  </Spin>
                </div>
              ) : (
                // 微课模式直接显示上传文件区域
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span>上传微课视频</span>
                  </div>
                  
                  <div style={{ 
                    padding: '20px', 
                    border: '1px dashed #d9d9d9', 
                    borderRadius: '4px',
                    background: '#fafafa',
                    textAlign: 'center'
                  }}>
                    <Upload
                      beforeUpload={handleFileUpload}
                      showUploadList={false}
                      accept="video/*"
                    >
                      <Button icon={<UploadOutlined />} size="large">
                        {uploadFile ? '重新选择视频文件' : '选择视频文件'}
                      </Button>
                    </Upload>
                    
                    {uploadFile && (
                      <div style={{ marginTop: '16px', fontSize: '14px', textAlign: 'left' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>已选择文件：</div>
                        <div style={{ backgroundColor: '#f0f7ff', padding: '12px', borderRadius: '4px', border: '1px solid #bae0ff' }}>
                          <div><strong>文件名：</strong>{uploadFile.name}</div>
                          <div><strong>文件大小：</strong>{(uploadFile.size / 1024 / 1024).toFixed(2)} MB</div>
                          <div><strong>视频时长：</strong>{videoDuration > 0 ? formatDuration(videoDuration) : '正在读取...'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* 根据不同模式显示不同的文件设置区域 */}
              {(selectedUploadClass || isMicroClass) && (
                <div style={{ marginTop: '24px' }}>
                  <Title level={5}>存档文件设置</Title>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'end', flexWrap: 'wrap' }}>
                    <Form.Item
                      name="filename"
                      label="存档文件名"
                      rules={[{ required: true, message: '请输入存档文件名' }]}
                      style={{ flex: 1, minWidth: '200px' }}
                    >
                      <Input placeholder="请输入存档文件名" />
                    </Form.Item>
                    
                    {isMicroClass && (
                      <Form.Item
                        name="duration"
                        label="视频时长（秒）"
                        rules={[{ required: true, message: '请输入视频时长' }]}
                        style={{ width: '200px' }}
                      >
                        <Input 
                          placeholder="视频时长（秒）" 
                          suffix="秒"
                          type="number"
                          min={1}
                        />
                      </Form.Item>
                    )}
                    
                    {!isMicroClass && (
                      <Form.Item label="选择视频文件">
                        <Upload
                          beforeUpload={handleFileUpload}
                          showUploadList={false}
                          accept="video/*"
                        >
                          <Button icon={<UploadOutlined />}>
                            {uploadFile ? uploadFile.name : '选择视频文件'}
                          </Button>
                        </Upload>
                        {uploadFile && (
                          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                            文件大小: {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        )}
                      </Form.Item>
                    )}
                  </div>
                </div>
              )}
              
              <div style={{ marginTop: '24px' }}>
                <Title level={5}>课堂存档信息</Title>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <Form.Item
                    name="grade_name"
                    label="届次"
                    rules={[{ required: true, message: '请选择届次' }]}
                    style={{ flex: 1, minWidth: '200px' }}
                    disabled={!uploadFile && isMicroClass}
                  >
                    <Select
                      placeholder="请选择届次"
                      options={gradeOptions}
                      style={{ width: '100%' }}
                      disabled={!uploadFile && isMicroClass}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="class_name"
                    label="班次"
                    rules={[{ required: true, message: '请选择班次' }]}
                    style={{ flex: 1, minWidth: '200px' }}
                    disabled={!uploadFile && isMicroClass}
                  >
                    <Select
                      placeholder="请选择班次"
                      options={classOptions}
                      style={{ width: '100%' }}
                      disabled={!uploadFile && isMicroClass}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="course_name"
                    label="课程"
                    rules={[{ required: true, message: '请选择课程' }]}
                    style={{ flex: 1, minWidth: '200px' }}
                    disabled={!uploadFile && isMicroClass}
                  >
                    <Select
                      placeholder="请选择课程"
                      options={courseOptions}
                      style={{ width: '100%' }}
                      listHeight={200}
                      showSearch
                      optionFilterProp="label"
                      disabled={!uploadFile && isMicroClass}
                    />
                  </Form.Item>

                  <Form.Item
                    name="custom_tags"
                    label="标签"
                    style={{ flex: 1, minWidth: '200px' }}
                    disabled={!uploadFile && isMicroClass}
                  >
                    <Select
                      mode="tags"
                      style={{ width: '100%' }}
                      placeholder="请选择或直接输入创建新标签"
                      options={tagOptions}
                      loading={tagLoading}
                      allowClear
                      maxTagCount={5}
                      listHeight={200}
                      disabled={!uploadFile && isMicroClass}
                    />
                  </Form.Item>
                </div>
              </div>
            </>
          )}
        </Form>
      </Modal>

      {/* 下载模式选择弹窗 */}
      <Modal
        title="选择下载模式"
        open={downloadModeModalVisible}
        onCancel={handleCancelDownloadMode}
        width={500}
        footer={[
          <Button key="cancel" onClick={handleCancelDownloadMode}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleConfirmDownloadMode}
            loading={loading}
            disabled={downloadMode === 'api' && !apiStatus.available}
          >
            确认提交
          </Button>,
        ]}
      >
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 'bold' }}>
            即将创建 {selectedHistoryClasses.length} 个存档任务，请选择下载模式：
          </div>
          
          <Radio.Group 
            value={downloadMode} 
            onChange={(e) => setDownloadMode(e.target.value)}
            style={{ width: '100%' }}
          >
            <div style={{ marginBottom: '16px' }}>
              <Radio value="api" style={{ fontSize: '16px' }}>
                <div style={{ marginLeft: '8px' }}>
                  <div style={{ fontWeight: 'bold', color: '#284D40' }}>
                    免费接口（推荐）
                    {apiStatus.remainingTasks !== null && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#999' }}>
                        剩余任务数：{apiStatus.remainingTasks}/{apiStatus.maxTasks}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                    使用破解的免费下载接口，与BOSS共享100个并发任务
                    {!apiStatus.available && (
                      <span style={{ color: '#ff4d4f', marginLeft: '8px' }}>
                        （当前不可用：{apiStatus.message}）
                      </span>
                    )}
                  </div>
                </div>
              </Radio>
            </div>
            
            <div>
              <Radio value="plaso" style={{ fontSize: '16px' }}>
                <div style={{ marginLeft: '8px' }}>
                  <div style={{ fontWeight: 'bold', color: '#E6A041' }}>
                    收费接口
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                    使用伯索官方付费接口，不限并发（其实是限制的，如任务堆积依然会排队处理），接口按导出课程总时长计费（1元/小时），如时间充裕，请尽量使用免费接口
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#fa8c16', 
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#fff7e6',
                    border: '1px solid #ffd591',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}>
                    💰 当前接口预估费用：{getEstimatedCost()} 小时 × 1元/小时 = {getEstimatedCost()} 元
                    <div style={{ fontSize: '12px', color: '#8c8c8c', fontWeight: 'normal', marginTop: '4px' }}>
                      总时长：{formatDuration(getTotalDuration())}
                      {getTotalDuration() > 0 && ''}
                      {getTotalDuration() === 0 && '，暂无课堂时长数据'}
                    </div>
                  </div>
                </div>
              </Radio>
            </div>
          </Radio.Group>
          
          {downloadMode === 'api' && selectedHistoryClasses.some(hc => !hc.fileId) && (
            <div style={{ 
              marginTop: '16px', 
              padding: '12px', 
              backgroundColor: '#fff2f0', 
              border: '1px solid #ffccc7',
              borderRadius: '6px'
            }}>
              <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: '4px' }}>
                ⚠️ 注意
              </div>
              <div style={{ color: '#ff4d4f', fontSize: '14px' }}>
                选中的课堂中有部分缺少fileId字段，无法使用自建API模式。
                请选择伯索API模式或重新选择包含fileId的课堂。
              </div>
            </div>
          )}
        </div>
      </Modal>
      
      {/* 编辑课堂存档信息对话框 */}
      <Modal
        title="编辑课堂存档信息"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleEditSubmit}
          >
            保存
          </Button>,
        ]}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Form.Item
            name="short_desc"
            label="课堂名称"
            rules={[{ required: true, message: '请输入课堂名称' }]}
          >
            <Input placeholder="请输入课堂名称" />
          </Form.Item>
          
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="grade_name"
              label="届次"
              rules={[{ required: true, message: '请选择届次' }]}
              style={{ flex: 1 }}
            >
              <Select
                placeholder="请选择届次"
                options={gradeOptions}
                style={{ width: '100%' }}
              />
            </Form.Item>
            
            <Form.Item
              name="class_name"
              label="班次"
              rules={[{ required: true, message: '请选择班次' }]}
              style={{ flex: 1 }}
            >
              <Select
                placeholder="请选择班次"
                options={classOptions}
                style={{ width: '100%' }}
              />
            </Form.Item>
            
            <Form.Item
              name="course_name"
              label="课程"
              rules={[{ required: true, message: '请选择课程' }]}
              style={{ flex: 1 }}
            >
              <Select
                placeholder="请选择课程"
                options={courseOptions}
                style={{ width: '100%' }}
                listHeight={200}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </div>
          
          <Form.Item
            name="is_reviewed"
            label="审核状态"
            rules={[{ required: true, message: '请选择审核状态' }]}
          >
            <Select
              placeholder="请选择审核状态"
              style={{ width: '100%' }}
            >
              <Select.Option value={true}>已审核</Select.Option>
              <Select.Option value={false}>未审核</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="custom_tags"
            label="标签"
          >
                                <Select
                      mode="tags"
                      style={{ width: '100%' }}
                      placeholder="请选择或直接输入创建新标签"
                      options={tagOptions}
                      loading={tagLoading}
                      allowClear
                      maxTagCount={5}
                      listHeight={200}
                    />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量编辑对话框 */}
      <Modal
        title={`批量编辑课堂存档信息 (${selectedTaskIds.length} 项)`}
        open={batchEditModalVisible}
        onCancel={() => setBatchEditModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setBatchEditModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleBatchEditSubmit}
            loading={batchLoading}
          >
            保存
          </Button>,
        ]}
      >
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f6f6f6', borderRadius: '6px' }}>
          <div style={{ color: '#666', fontSize: '14px' }}>
            <strong>批量编辑说明：</strong>
            <br />• 只填写需要修改的字段，空白字段将保持原值不变
            <br />• 课堂名称不支持批量修改，需要单独编辑
            <br />• 标签操作：可选择"清空标签"或"设置新标签"，清空标签优先级更高
            <br />• 如果既不清空也不设置标签，则保持原有标签不变
          </div>
        </div>
        
        <Form
          form={batchEditForm}
          layout="vertical"
        >
                     <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
             <Form.Item
               name="grade_name"
               label="届次"
               style={{ flex: '1 1 200px', minWidth: '200px' }}
             >
               <Select
                 placeholder="选择届次（不选择则保持原值）"
                 options={gradeOptions}
                 style={{ width: '100%' }}
                 allowClear
               />
             </Form.Item>
             
             <Form.Item
               name="class_name"
               label="班次"
               style={{ flex: '1 1 200px', minWidth: '200px' }}
             >
               <Select
                 placeholder="选择班次（不选择则保持原值）"
                 options={classOptions}
                 style={{ width: '100%' }}
                 allowClear
               />
             </Form.Item>
             
             <Form.Item
               name="course_name"
               label="课程"
               style={{ flex: '1 1 200px', minWidth: '200px' }}
             >
               <Select
                 placeholder="选择课程（不选择则保持原值）"
                 options={courseOptions}
                 style={{ width: '100%' }}
                 allowClear
                 listHeight={200}
                 showSearch
                 optionFilterProp="label"
               />
             </Form.Item>
           </div>
          
                     <Form.Item
             name="is_reviewed"
             label="审核状态"
           >
             <Select
               placeholder="选择审核状态（不选择则保持原值）"
               style={{ width: '100%' }}
               allowClear
             >
               <Select.Option value={true}>已审核</Select.Option>
               <Select.Option value={false}>未审核</Select.Option>
             </Select>
           </Form.Item>
           
                      <Form.Item
             name="clear_tags"
             label="标签操作"
             valuePropName="checked"
           >
             <div>
               <div style={{ marginBottom: '8px' }}>
                                   <input
                    type="checkbox"
                    id="clear_tags_checkbox"
                    checked={clearTagsChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setClearTagsChecked(checked);
                      batchEditForm.setFieldValue('clear_tags', checked);
                      if (checked) {
                        // 如果选择清空标签，则清空标签选择
                        batchEditForm.setFieldValue('custom_tags', undefined);
                      }
                    }}
                    style={{ marginRight: '8px' }}
                  />
                 <label htmlFor="clear_tags_checkbox" style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                   清空所有选中任务的标签
                 </label>
               </div>
               <div style={{ fontSize: '12px', color: '#999', marginBottom: '12px' }}>
                 勾选此选项将清空所有选中任务的标签，优先级高于下方的标签选择
               </div>
             </div>
           </Form.Item>
           
           <Form.Item
             name="custom_tags"
             label="标签设置"
             extra="注意：如果填写标签，将会完全替换所有选中任务的原有标签"
           >
             <Select
               mode="tags"
               style={{ width: '100%' }}
               placeholder="选择或输入标签（不填写则保持原标签不变）"
               options={tagOptions}
               loading={tagLoading}
               allowClear
               maxTagCount={5}
               listHeight={200}
               disabled={clearTagsChecked}
             />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 详情抽屉 */}
      <Drawer
        title="课程详情"
        placement="right"
        width={500}
        onClose={() => setDetailDrawerVisible(false)}
        open={detailDrawerVisible}
      >
        {currentTask && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>课程描述</div>
              <div>{currentTask.short_desc}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>教师信息</div>
              <div>ID: {currentTask.teacher_id}</div>
              <div>姓名: {currentTask.teacher_name}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>课堂存档信息</div>
              <div>届次: {currentTask.grade_name}</div>
              <div>班次: {currentTask.class_name}</div>
              <div>课程: {currentTask.course_name}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>状态</div>
              <Tag color={getStatusColor(currentTask.status)}>
                {getStatusText(currentTask.status, currentTask)}
              </Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>时间信息</div>
              {currentTask.course_start_time && <div>课程开始时间: {currentTask.course_start_time}</div>}
              <div>创建时间: {currentTask.create_time}</div>
              {currentTask.finish_time && <div>完成时间: {currentTask.finish_time}</div>}
              {currentTask.create_time && currentTask.finish_time && (
                <div>耗时: {calculateDuration(currentTask.create_time, currentTask.finish_time)}</div>
              )}
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>操作人</div>
              <div>{currentTask.operator_name || '-'}</div>
            </div>
            {currentTask.download_url && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>下载链接</div>
                <div style={{ wordBreak: 'break-all' }}>{currentTask.download_url}</div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default DownloadList; 
